import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useParams, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useApolloClient, useQuery } from "@apollo/client";
import { toast } from "sonner";

import { useAuthContext } from "../auth/AuthContext";
import { trackEvent } from "../telemetry/appInsights";
import { useCurrentUser } from "../auth/CurrentUserContext";
import { decodeJwtClaims, ppUserIdFromClaims, ppUserRoleFromClaims } from "../auth/jwt";
import { useMember360Token } from "../auth/useMember360Token";
import { useSessionEvents } from "../auth/useSessionEvents";
import {
  ensurePpAccessToken,
  getPpToken,
  hasPersistedPpLink,
  isPpB2cConfigured,
  resolveCreateAuthIdentityToken,
  resolveIdentityEmail,
} from "../auth/ppToken";
import { PP_ELIGIBILITY_REASON_TEXT } from "../auth/ppEligibility";
import { PpLoginGate, PP_LOGIN_CANCELLED_MESSAGE } from "../auth/ppLoginGate";
import PpLoginModal from "../auth/PpLoginModal";
import { usePpCreateAuthPreflight } from "./usePpCreateAuthPreflight";
import { useActingUserIpa } from "./useActingUserIpa";
import type { AuthProviderId } from "../auth/authProviders";
import LoadingSpinner from "../components/LoadingSpinner";
import { GraphQLClientGate } from "../lib/apollo/GraphQLClientGate";
import { useDarkMode } from "../theme/useDarkMode";
import { MEMBER_BY_ID, type MemberByIdResult, type MemberByIdVars } from "./graphql/memberById";
import {
  CONNECT_PATIENT_IDS,
  selectHealthPlanMemberId,
  type ConnectPatientIdsResponse,
  type ConnectPatientIdsVars,
} from "./graphql/connectPatientIds";
import {
  parsePpMemberId,
  isPpMemberUnresolvable,
  shouldResolvePpMemberIdFallback,
} from "./parsePpMemberId";
import { toCareManagementMarket } from "./careManagementMarket";
import { toPatientObject } from "./toPatientObject";
import {
  SUGGESTED_QUESTIONS,
  useMemberAssistantSend,
} from "./useMemberAssistantSend";
import { useQualityGapClosures } from "./useQualityGapClosures";
import {
  useVisibleModules,
  useVisibleHomeCards,
  useActionAllowed,
  useModuleActions,
  useModuleEnabled,
  useTabOrder,
  useConfiguredDefaultTab,
} from "../access/useFeatureAccess";
// DOCUMENTS_TAB is imported from the catalog rather than re-declared here (it
// used to be a second, identical local const): the catalog's `Documents` entry
// keys off this exact string for the persona gate + tab-order reverse lookup, so
// one shared constant makes page/catalog drift impossible.
import { DOCUMENTS_TAB } from "../config/featureCatalog";
import { useInitialTab } from "./useInitialTab";
import type { SearchFilters } from "./MemberResultsTable";
import { MemberDocumentsPage } from "./Documents";
import { featureFlags } from "../config/featureFlags";
import { astranaIdFromNav } from "./graphql/resolveEhrMember";
import { visitNoteMemberIdentity } from "./notes/visitNoteMemberIdentity";
import { useEhrPatientSwitch } from "./patientSwitch/useEhrPatientSwitch";
import { useElationBridge } from "../bridge/useElationBridge";
import { requestElationRefresh, requestElationWrite } from "../bridge/extensionBridge";
import { toEhrEncounterSnapshot } from "./notes/toEhrEncounterSnapshot";
import { createWriteSectionHandler } from "./notes/visitNoteWriteSection";
import { isBpRecordingsVisible } from "./bpRecordingsVisible";
import { clinicalSummaryTitleOverride } from "./clinicalSummaryTitleOverride";
import { AmbientScribePanel, LeaveNotePrompt } from "@astrana/ui/scribe";
import { NotesTab } from "./notes/NotesTab";
import { SwitchPatientModal } from "./patientSwitch/SwitchPatientModal";
import { shouldAutoNavigateOnSwitch } from "./patientSwitch/switchDecision";
import { resetAutoNav } from "./patientSwitch/autoNav";
import {
  toClaimMemberId,
  toClaimMemberFromSeed,
  type PpClaimMemberSeed,
} from "./toClaimContext";
import CreateClaimGate from "../claims/CreateClaimGate";
import {
  claimGateKey,
  claimProviderStillValidForMember,
  type ClaimProvider,
} from "../claims/claimProviderList";
import { toBcp47 } from "../translator/patientLanguage";
import { createTranslatorCredentials } from "../translator/translatorCredentials";

// eslint-disable-next-line import/no-unresolved
import {
  CreateClaim,
  MemberProfile,
  ModuleType,
  graphqlCareManagementSeam,
  Translator,
  type Case,
  type ModuleGroup,
  type ModuleObject,
  type PatientObject,
} from "@astrana/ui/modules";
// eslint-disable-next-line import/no-unresolved

// CaseDetail and MemberSMS are host-side render targets (not @astrana/ui
// exports — CaseDetail is what `onCaseClick` returns, and MemberSMS still has
// no counterpart in @astrana/ui as of v0.4.36, confirmed against the shipped
// dist). Stubbed here until the corresponding host feature / lib export exists.
function CaseDetail(_props: { caseId: string }) {
  return null;
}
function MemberSMS(
  _props: {
    memberId: string;
    memberName?: string;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    hideStrip?: boolean;
  },
) {
  return null;
}
// eslint-disable-next-line import/no-unresolved
import { GraphQLProvider } from "@astrana/ui/provider";
import { resolvePpMembId, PpMemberResolutionError } from "./resolvePpMembId";
import { toAuthMemberFromPathways, toAuthMemberFromPp } from "./toAuthMember";
import { decideNoteLeaveRequest } from "./noteLeavePrompt";
import { useReturnFocusRefresh } from "./useReturnFocusRefresh";
import {
  UNSAVED_CHANGES_CONFIRM_MESSAGE,
  useUnsavedChangesGuard,
} from "./useUnsavedChangesGuard";

const GRAPHQL_URL = import.meta.env.VITE_CARE_MANAGEMENT_GQL_ENDPOINT ?? "";
const ENVIRONMENT = (import.meta.env.VITE_ENVIRONMENT || "development") as
  | "production"
  | "staging"
  | "qa"
  | "development";

// ACT-1253 Phase-1b — the withheld click-to-call seam. Base URL of the Amazon
// Connect instance whose embedded CCP dials outbound calls (e.g.
// `https://astrana.my.connect.aws/connect`). Unset ⇒ the Outreach "Call member"
// affordance never renders (log-only, as today). Non-prod instances open Connect
// sign-in in an auto-closing popup; prod shows manual login instructions.
const AMAZON_CONNECT_INSTANCE_URL =
  import.meta.env.VITE_AMAZON_CONNECT_INSTANCE_URL || undefined;
const AMAZON_CONNECT_LOGIN_POPUP = ENVIRONMENT !== "production";

// Demo mock for the Claims module's "Submit Claim" button. Presence of
// `onSubmitClaim` is what astrana-ui uses to decide whether to render
// the button and its inline <SubmitClaimForm>; without the callback the
// Claims tab is list-only. We don't have a real claim-submission
// endpoint wired, so sleep briefly and resolve — the form closes on
// its own, which is feedback enough for a demo. Mirrors Provider
// Portal's `handleSubmitClaim` in components/Member360Panel/index.tsx.
async function mockSubmitClaim(_draft: unknown): Promise<void> {
  await new Promise((r) => setTimeout(r, 600));
}

const INITIAL_TAB = "Home";

/**
 * REMOVED (2026-07-24): this used to be a fake `"pp-auth-pending-awaiting-
 * login"` string handed to @astrana/ui's Authorizations module as
 * `accessToken` so `canSelfHostEmbed = truthy(ppWebBaseUrl && ppApiBaseUrl &&
 * accessToken)` would read true (rendering Create/Duplicate/Modify) for an
 * eligible-but-tokenless user, on the theory that `ensurePpSession` would
 * swap in a real token before mint (see git history for the old doc comment
 * / safety proof, still valid for the MINT step in isolation).
 *
 * Confirmed live (temp debug logging, SSO→PP-login→open-Create-Auth repro):
 * `canSelfHostEmbed()`'s truthiness check has NO way to distinguish "a real
 * bearer" from "any non-empty string" — so the moment this sentinel made it
 * truthy, the module mounted immediately and fired its OWN eager,
 * `resolvePpMemberId`-independent read/init queries (GetAuthMember plus
 * reference-data lookups: ExpiryDays, GetDownloadableResources, MediCalCode,
 * RuleCodes, GetReferType, GetIpaAdvise, TempAuthNumber, ModifierCodes,
 * ListOfSpecialty, POS) using the sentinel as the literal `Authorization`
 * header. Every one of those 401'd and was NEVER retried once the real
 * token landed a render later (`ensurePpSession`'s live-ref/flushSync
 * guarantee only protects the click-driven MINT step, not these mount-time
 * reads) — a storm of 401s on every SSO→PP-login→open-Create-Auth flow.
 *
 * Fix: `resolvedAccessToken` below is now EITHER the real `ppToken` OR
 * `undefined` — never a placeholder. The module can no longer mount (or
 * fire any query) without a genuine bearer. The remaining problem this
 * sentinel solved — giving a NEVER-linked, tokenless-but-eligible identity
 * some way to ever trigger `ensurePpSession`/PpLoginModal, since
 * `canSelfHostEmbed()` can now never go true for them via the module itself
 * — is solved by a host-injected `actions` entry on the Auths ModuleObject
 * (see `showPpLinkAction` below), a genuine in-place label swap in the SAME
 * header slot the module's own "Create" button occupies — NOT a separate
 * floating banner and NOT the module's `onCreateAuth` fallback (whose button
 * text is hardcoded module copy we can't relabel).
 */

/** Matches @astrana/ui's mintHandoffToken failure message template
 * (`Handoff mint failed (${status}).`, providerPortalHandoff.ts) for ANY
 * status code. Kept as a defensive fallback for the `onError` wrapper below
 * even though the sentinel that used to make this the EXPECTED failure mode
 * is gone (see the removed-sentinel note above): Modify still has no native
 * impl and always renders the module's iframe embed, which reads the live
 * `accessToken` ref at MINT time with no pre-mint seam to hook — so a real
 * token that expires/is invalidated in the split second between render and
 * mint could still produce this exact failure shape. Pairing it with
 * "we currently hold no real PP token" (`noRealPpToken` below) is what keeps
 * the signal reliable even without a sentinel to expect it from. */
const MINT_FAILURE_PATTERN = /Handoff mint failed \(\d+\)\.?/;

/**
 * How long the "needs to link Provider Portal" unresolved condition
 * (`ppEligible && !ppToken && !ppPreflight.hasLink`) must hold CONTINUOUSLY
 * before the Auths header's action swaps its label over to the link flow —
 * see `showPpLinkAction`'s own doc comment below. Long enough to swallow
 * normal sub-second login-hydration flicker (an already-linked SSO identity,
 * or a provider-portal-b2c PRIMARY user whose `primarySessionToken` hydrates
 * a render late), short enough to still promptly offer a genuinely-unlinked
 * identity the link action.
 */
const PP_LINK_ACTION_DEBOUNCE_MS = 1_500;

// Mirrors Provider Portal's Member360Panel layout — Home and the same
// structural tabs. Top-level requestor is "pathways" so modules without an
// explicit override (Auths, Labs, etc.) use the DBX query path.
// Claims/Medications keep an explicit "provider-portal" override to mirror
// PP's EZCap branches.
//
// HOME_CARDS_DEFAULT + OTHER_MODULES together are the master ordered list
// (formerly a single ALL_MODULES). useVisibleHomeCards() (ACT-1303) filters
// and re-orders HOME_CARDS_DEFAULT per the active persona's server-driven
// `raw.currentPersona.modules[]` config; useVisibleModules() (from the access
// layer) then filters the resulting Home group + OTHER_MODULES through the
// FEATURE_CATALOG tab→moduleId mapping + the active persona's gate at render
// time. Order is preserved end to end.
//
// Tab → moduleId mapping notes (owned by FEATURE_CATALOG, not this file):
//   "Diagnoses" / "Labs" map to no module id → always visible.
//   Care Team is NO LONGER A TAB — it is a Home CARD ("HomeCareTeam"), so no
//   tabName maps to the "CareTeam" gating id any more. That catalog entry is
//   deliberately left in place (live `persona_modules` rows and the
//   PERSONA_MODULES seed still reference it), but it now gates nothing here;
//   retiring it needs a matching astraconnect-admin-console change.

// Module-level constant (stable reference) — the Home tab's 8 always-mounted
// cards in their default/fallback order, BEFORE persona filtering.
// useVisibleHomeCards reads HOME_CARDS_DEFAULT (derived from this just below,
// also module-level) identity-stably across renders since neither changes.
// Demographics is FIRST here, Care Team second; the flag-gated Next Best Action
// card is prepended ahead of them in HOME_CARDS_DEFAULT when enabled.
// Demographics, like the other 7, is now admin-configurable (ACT-1303-style) via
// the backend's `Home*` module ids — it is no longer spliced in as an
// always-on special case (see the removed `homeCardsWithDemographics`, and
// the `defaultExpanded` prop injected below from HomeDemographics's actions).
//
// `satisfies` (not a `: ModuleObject[]` annotation) is deliberate: annotating
// the array up front makes TS check each object literal top-down against the
// full 17-member ModuleObject union, which loses the CASES entry's
// discriminant narrowing and misreports its `props.actions` as an excess
// property. `satisfies` infers each literal's own type bottom-up first (so
// CASES narrows to CaseActionProps correctly) and only then verifies the
// whole array conforms to ModuleObject[] — narrower and still type-safe.
const HOME_CARDS_BASE: NonNullable<ModuleGroup["modules"]> = [
  { type: ModuleType.DEMOGRAPHICS },
  // Care Team is a Home CARD, not a tab (product directive: "Care Team can
  // just be auto-collapsed row either beneath Demographics or at the bottom").
  // `collapsible` is @astrana/ui #664 (v0.5.44+); it renders the same accordion
  // pattern Demographics uses, so the two rows stack identically. Its default
  // open state is admin-configurable per persona — see homeCareTeamActions below.
  { type: ModuleType.CARE_TEAM, props: { collapsible: true } },
  { type: ModuleType.CLINICAL_SUMMARY },
  { type: ModuleType.INPATIENT_ED },
  { type: ModuleType.ANNUAL_WELLNESS },
  { type: ModuleType.QUALITY_GAPS },
  { type: ModuleType.HCC },
  {
    type: ModuleType.CASES,
    props: {
      onCaseClick: (caseItem: Case) => <CaseDetail caseId={caseItem.id} />,
      // T4 — Care Management. The programs panel and the New Referral
      // dialog live in @astrana/ui's Cases module behind its additive
      // `careManagement` prop (see that module's careManagement.seam.ts).
      // The pin now carries it (v0.5.70), so the old placeholder `actions`
      // entry that fired a window.alert is gone — the module renders its own
      // button and its own portaled dialog. Do NOT re-add a "New Referral"
      // entry to `actions`: with `showNewReferral` on, that renders TWO
      // buttons.
      //
      // `programsView: "table"` matches what Provider Portal shows at its two
      // CASES-bearing integration points (read-only PROGRAM / ELIGIBILITY /
      // ENROLLMENT STATUS). `hideCases` is deliberately NOT set — unlike PP,
      // this is the app's only Cases surface, so the case list stays. The
      // seam is `graphqlCareManagementSeam` (@astrana/ui v0.5.98): status
      // changes and referrals persist through care-management-gql via the
      // shared Apollo singleton that <GraphQLProvider> installs. A backed seam
      // has no `unavailableReason`, so the "Preview only" banner disappears and
      // the dropdowns/submit enable themselves. Member identity comes from the
      // `patient` passed to <MemberProfile> (see toPatientObject / the patient
      // literal near the bottom of this component).
      careManagement: {
        seam: graphqlCareManagementSeam,
        showPrograms: true,
        programsView: "table" as const,
        showNewReferral: true,
      },
    },
  },
];

// CM-8319 — the engine-selected Next Best Action card, prepended FIRST because
// it is the only card that asks the user to act. Keep this order in sync with
// HOME_CARD_TYPES in featureCatalog.ts, which documents the same default order
// for the admin console's benefit. (HOME_CARD_TYPES has no runtime consumer in
// this app today — THIS array is the actual render list, and the no-persona
// fallback in useVisibleHomeCards is "return the cards it was handed", i.e.
// this one. So a card absent here cannot render, whatever HOME_CARD_TYPES says.)
//
// GATED OFF BY DEFAULT (featureFlags.nextBestAction). Not mounting the card is
// the real off-switch — the persona gate FAILS OPEN, so a persona grant cannot
// be relied on to keep it off screen. Never mount it in an environment whose
// care-management-gql lacks `recordNbaDirected`, or the card's "Go to <tab>" CTA
// hits a missing mutation.
//
// Prepended here rather than spread into the literal above on purpose: a spread
// inside that array widens each entry's `type` to the whole ModuleType enum,
// which breaks the discriminant narrowing the CASES entry's `props` depends on
// (see the typing note above HOME_CARDS_BASE).
const HOME_CARDS_DEFAULT: NonNullable<ModuleGroup["modules"]> =
  featureFlags.nextBestAction
    ? [{ type: ModuleType.NEXT_BEST_ACTION }, ...HOME_CARDS_BASE]
    : HOME_CARDS_BASE;

// The other 7 tab groups, unchanged order — not subject to Home-card
// filtering (only the whole-tab gate in useVisibleModules applies to these).
// AC-ADM-3: Diagnoses and Labs are no longer special. They now carry real
// FEATURE_CATALOG entries and real persona_modules rows (migration 0027), so
// they are persona-gated and admin-reorderable like every sibling here.
const OTHER_MODULES_BASE: ModuleGroup[] = [
  { tabName: "Auths", modules: [{ type: ModuleType.AUTHORIZATIONS }] },
  { tabName: "Eligibility", modules: [{ type: ModuleType.ELIGIBILITY }] },
  {
    tabName: "Claims",
    modules: [
      {
        type: ModuleType.CLAIMS,
        props: {
          requestor: "provider-portal",
          onSubmitClaim: mockSubmitClaim,
        },
      },
    ],
  },
  { tabName: "Medications", modules: [{ type: ModuleType.MEDICATIONS, props: { requestor: "provider-portal" } }] },
  { tabName: "Diagnoses", modules: [{ type: ModuleType.DIAGNOSES }] },
  { tabName: "Labs", modules: [{ type: ModuleType.LABS }] },
  { tabName: "Outreach", modules: [{ type: ModuleType.OUTREACHES }] },
];

// CM-8310 / CM-8319 — the member's completed next-best-action history, sourced
// from nba_completion. Gated off by default alongside the card
// (featureFlags.nextBestAction): the feed is empty and meaningless without the
// engine, and not mounting the tab is the only reliable gate — see the
// HOME_CARDS_DEFAULT note above for why this is appended rather than spread in.
const OTHER_MODULES: ModuleGroup[] = featureFlags.nextBestAction
  ? [
      ...OTHER_MODULES_BASE,
      { tabName: "Activity", modules: [{ type: ModuleType.AGENT_ACTIVITY }] },
    ]
  : OTHER_MODULES_BASE;

/**
 * PP member fields carried from the search results into the full profile via
 * react-router `location.state` (see MemberResultsTable). Mirrors
 * providerPortalFrontend's Member360Panel patient construction.
 */
interface PpMemberState {
  companyId: string;
  membMpiNo: string;
  membKeyId?: string | null;
  membId?: string | null;
  fullName?: string | null;
  startDate?: string | null;
  /**
   * Health plan code (PP's `RmiHpCode`) — the member's HEALTH PLAN, NOT the
   * IPA/tenant (`companyId`). Required, non-empty, by
   * `ClaimMember/CreateClaim`, and the input to the Create Claim entry gate's
   * health-plan block rules (claims/claimMemberAccess.ts).
   *
   * ABSENT on a TYPED URL / BOOKMARK / NEW TAB — but NOT on a plain reload.
   * react-router stores `location.state` in `window.history.state`, which the
   * browser restores for the same history entry, so F5 keeps this field; only a
   * navigation that creates a fresh entry with no state loses it. The PP route id
   * carries only `companyId:membMpiNo:membKeyId`, and this value is DELIBERATELY
   * not added to it — the pathname is persisted to localStorage (`member-360:last-route`),
   * and a member's health plan is PHI-adjacent in a way the opaque GUIDs are
   * not. It is also deliberately NOT re-derived from
   * `connectPatient.eligibility.insuranceV2.healthPlanCode` (the DBX/connect
   * warehouse), which is a DIFFERENT source from PP/EZCap's RmiHpCode and can
   * carry multiple spans — silently seeding a claim with a wrong health plan is
   * worse than not offering the claim at all. So on that path the Create Claim
   * gate BLOCKS rather than sending an empty hpCode (which is an HTTP 400) or a
   * guessed one. Its copy offers "reopen from Member Search" only as one of the
   * possibilities, because the same block is also reached by members who simply
   * have no health plan on record, for whom that instruction is useless.
   */
  healthPlanCode?: string | null;
}

interface LocationState {
  searchFilters?: SearchFilters;
  /**
   * The URL the profile was opened FROM, when it's a surface other than Member
   * Search that wants "Back" to return to it (e.g. the Practice Schedule sets
   * `from: "/schedule?date=…&providerNpi=…"`). handleBack routes here instead
   * of /members so back-navigation lands on the same schedule day/provider.
   */
  from?: string;
  ppMember?: PpMemberState;
  /**
   * Connect-only fallback member (valid in connect.patient_profile, absent from
   * Mongo). Carries the astranaId so the profile can render via the library's
   * astranaId path without a MEMBER_BY_ID lookup — mirrors ppMember for the
   * pp: path. On a hard refresh this is gone; the astranaId is recovered from
   * the `astrana:<astranaId>` route id.
   *
   * `companyId`/`memberId`/`membKeyId` are OPTIONAL extras a host can carry
   * alongside astranaId when the connect-only member is ALSO a real PP/EZCAP
   * member (e.g. WorklistPage's PatientPriorityIndex row-click — those rows
   * come off the provider's PP panel and always have a companyId/memberId,
   * they just navigate via the astrana: fallback route because astranaId is
   * the only identifier guaranteed on every row). Without these, an
   * astrana:-opened member has NO company/member context at all (see
   * `companyId`/`handoffMemberIdHint` below), which is correct for a genuine
   * connect-only match but starves the Create-Auth preflight
   * (usePpCreateAuthPreflight) for a worklist-opened member that really does
   * have a company context — surfacing as a spurious "no company context"
   * toast after usePpCreateAuthPreflight's grace window. Absent on a hard
   * refresh (location.state is gone) — Create Auth then degrades to the
   * existing connect-only "no context" behavior, same as before this field
   * existed.
   */
  astranaMember?: {
    astranaId: string;
    companyId?: string;
    memberId?: string;
    membKeyId?: string;
    /**
     * `"Last, First"` display name and `MM/DD/YYYY` DOB (v0.4.51+
     * PatientPriorityIndexSelectedMember) — the worklist's own row data,
     * carried because an astrana:-only member SKIPS the Pathways member
     * query that would otherwise resolve `loadedMember.name`/`dob`. Without
     * these the Create-Auth PP crosswalk (resolvePpMembId, keyed on
     * name + DOB + IPA) has nothing to search on and fails loud. Absent on a
     * hard refresh (location.state is gone), same degrade-to-today's-behavior
     * caveat as the other astranaMember extras above.
     */
    name?: string;
    dob?: string;
  };
  /** Elation patient id this profile was opened for (from an EHR match). */
  ehrPatientId?: string | null;
}

export interface MemberProfilePageProps {
  /**
   * P6b keep-alive tab shell: when the profile lives UNDER the shared
   * TabbedAppShell header (which already provides the theme toggle + account/
   * persona menu), suppress MemberProfile's own duplicate global-chrome
   * affordances so they don't render twice. The patient-identity content, the
   * "Back to Home" affordance, SMS, and the assistant are unaffected. Only
   * meaningful on standalone web with VITE_FEATURE_STATEFUL_TAB_SHELL on.
   */
  hideGlobalChrome?: boolean;
  /**
   * Keep-alive override for the route `:id`. The tab shell keeps this profile
   * MOUNTED (and thus reflecting the last-opened member) even while the browser
   * URL is on another tab (`/members`, `/worklist`), where `useParams().id`
   * would be undefined. When provided, this pins the member the profile renders.
   */
  navIdOverride?: string;
  /**
   * Keep-alive override for the router `location.state` (the in-memory
   * ppMember / astranaMember / searchFilters payload the profile was opened
   * with). Pinned alongside `navIdOverride` so a hidden-but-mounted profile
   * doesn't read a different tab's location state. `undefined` = use the live
   * router state (classic behavior).
   */
  locationStateOverride?: LocationState | null;
  /**
   * P6d multi-tab shell (browser-tab-style patient tabs) — fires once a real
   * member NAME becomes available for this profile instance, so a tab opened
   * without a name in hand (a hard refresh/deep link to `/members/:id`, or a
   * Worklist row — whose selected-member payload carries no name field at
   * all) can upgrade its placeholder tab title once resolved.
   *
   * Sourced from the SAME locally-known name MemberSMS's `memberName` prop
   * already uses below (`ppMember.fullName` / `loadedMember.name`) — NOT a
   * new @astrana/ui seam; astrana-ui-modules' MemberProfile exposes no
   * "patient resolved" callback to read from instead. Connect-only
   * (`astrana:`) members have no such local source and keep the placeholder
   * title — a documented gap, same posture as the `referringProvider` GAP
   * comment above.
   */
  onMemberResolved?: (name: string) => void;
  /**
   * Whether this profile is the ACTIVE (visible) tab in the keep-alive shell.
   * The shell mounts every open profile at once (hidden via CSS); a hidden tab
   * still runs its effects, so without this gate every open profile would drive
   * the EHR patient-switch auto-nav off the shared Elation bridge and fight over
   * navigation. Only the active tab may auto-navigate. Defaults true — in the
   * non-shell path the profile is only mounted when it IS the active view.
   */
  isActiveTab?: boolean;
}

export default function MemberProfilePage({
  hideGlobalChrome = false,
  navIdOverride,
  locationStateOverride,
  onMemberResolved,
  isActiveTab = true,
}: MemberProfilePageProps = {}) {
  const params = useParams<{ id: string }>();
  const id = navIdOverride ?? params.id;
  const navigate = useNavigate();
  const routerLocation = useLocation();
  // Effective router state: the pinned override (keep-alive tab shell) or the
  // live router state (classic route). `undefined` override → live state.
  const effectiveState = (
    locationStateOverride !== undefined
      ? locationStateOverride
      : (routerLocation.state as LocationState | null)
  ) as LocationState | null;
  const { token } = useMember360Token();
  const apolloClient = useApolloClient();
  const {
    logout,
    accountLabel,
    selectedProvider,
    accessToken: primarySessionToken,
  } = useAuthContext();
  // AM-5828 — what @astrana/ui's <GraphQLProvider> treats as "a different
  // signed-in person" for Apollo cache partitioning. Changing it rebuilds the
  // client and drops the InMemoryCache; a mere token rotation must NOT change
  // it. We pass it explicitly rather than letting the lib derive it from the
  // bearer's subject claim, because the extension bridge can swap the Entra
  // account underneath us and the app knows that before the claims do.
  const apolloIdentityKey = `${selectedProvider ?? "none"}:${accountLabel ?? "anon"}`;
  const { triggerExpiryWarning } = useSessionEvents();
  const { isDark, toggleTheme } = useDarkMode();
  // ACT-1303 — persona/module config drives tab + Home-card visibility. On the
  // INITIAL load the currentUser query is in flight with no resolved role yet, so
  // `canSeeModule`/`useVisibleModules` would see a null persona and fall back to
  // "all visible". Gate the profile body on that initial load only (see the
  // return-level guard below) so tabs/cards never flash the allow-all fallback
  // before the first persona resolves — WITHOUT re-blanking on later `loading`
  // pulses (a background refetch / persona switch), which would tear down the
  // embedded MemberProfile subtree. Mirrors App.tsx ProtectedRoute's
  // `loading && role === null` initial-only gate.
  const {
    loading: currentUserLoading,
    role: currentUserRole,
    raw: currentUserRaw,
  } = useCurrentUser();
  const [smsOpen, setSmsOpen] = useState(false);
  // AC-TRX-1 — the translator rail is controlled by the host and starts closed;
  // the component's own always-visible trigger strip is what opens it.
  const [translatorOpen, setTranslatorOpen] = useState(false);
  // Persona gate for the rail. Server-driven (`currentPersona.modules[]`) via
  // the canonical canSeeModule closure — which persona may interpret for a
  // patient is DB/admin-console configuration, NOT a hardcoded list here. The
  // PERSONA_MODULES seed carries Provider + Medical Assistant as the PRD
  // baseline the DB grants are checked against; the DB is what actually decides.
  // Inherits the same fail-closed semantics as every other gated surface: a
  // currentUser query error denies, and a user with no persona follows the
  // VITE_FEATURE_PERSONA_ENFORCEMENT seam.
  const translatorAllowed = useModuleEnabled("Translator");
  // NOTE (AC-ADM-3): the Notes tab's own `useModuleEnabled("Notes")` gate (G1)
  // is GONE from here — not weakened. Notes is now merged into the tab list
  // BEFORE the single `useVisibleModules` pass (see `allModulesWithExtras` /
  // `visibleModulesWithExtras` below), so its "Notes" tabName is gated by the
  // catalog reverse lookup exactly like every other tab. One gate path, not two.
  // Credential getters are identity-stable per Apollo client so the rail never
  // sees a new function on every render (it holds them across a live session).
  const translatorCredentials = useMemo(
    () => createTranslatorCredentials(apolloClient),
    [apolloClient],
  );

  // Emit member_profile_opened on mount and on in-profile member→member
  // navigation (the route doesn't remount when only :id changes). Using [id]
  // as the dep fires once per distinct member viewed — no PHI in props (ACT-1037).
  useEffect(() => { trackEvent("member_profile_opened"); }, [id]);

  // Create-Auth seamless PP login (ACT-1247 follow-up) — see the
  // removed-sentinel note above for the full mechanism history. `ppLoginOpen`
  // drives the modal; `ppLoginGateRef` is the idempotent resolver
  // (auth/ppLoginGate.ts) — a second `ensurePpSession()` call while one is
  // already pending (e.g. the "Link Provider Portal" banner click firing at
  // the same time as the onError-triggered retry-prompt for Modify) shares
  // the SAME promise instead of racing a second modal open / orphaning the
  // first resolver.
  const [ppLoginOpen, setPpLoginOpen] = useState(false);
  // ACT-1247 SSO-PP auth-linking Phase 1 — bump-only counter to force a
  // synchronous re-render after a SILENT ensurePpAccessToken refresh (no
  // modal), mirroring handlePpLoginSuccess's flushSync pattern below so
  // resolvedAccessToken picks up the refreshed token before ensurePpSession's
  // caller continues past its await. The value itself is never read.
  const [, setPpTokenRefreshTick] = useState(0);
  const ppLoginGateRef = useRef<PpLoginGate | null>(null);
  if (!ppLoginGateRef.current) {
    ppLoginGateRef.current = new PpLoginGate({ onOpen: () => setPpLoginOpen(true) });
  }
  // Live context for the stable ensurePpSession callback below — same
  // ref-mirrors-latest-render pattern as embedCtxRef further down (populated
  // every render so the stable callback never closes over stale values).
  const ppSessionCtxRef = useRef({
    selectedProvider: null as AuthProviderId | null,
    sessionAccessToken: null as string | null,
    identityEmail: undefined as string | undefined,
  });

  // ACT-1247 (Phase 4) — `refreshKey` feeds the remount key on the
  // <GraphQLClientGate> wrapping <MemberProfile> below.
  //
  // AM-6260: the hook's `refreshNow()` arm is deliberately NOT destructured any
  // more — it now has zero call sites. Both handlers that used to call it
  // (`stableOnAuthCreated`, `stableOnClaimCreated`) are no-ops precisely
  // BECAUSE bumping this key remounts the whole subtree, which wiped the
  // module's post-submit confirmation screen and reset the active tab to Home.
  // See those handlers for the full rationale. `refreshKey` is kept in the gate
  // key as the seam for a future non-destructive refresh; nothing bumps it
  // today, so the key is effectively constant.
  //
  // (AM-5828 handoff purge: `useReturnFocusRefresh` used to also expose a
  // `markHandoffPending` arm consumed by focus/visibility listeners, for a
  // since-abandoned "open PP in a new tab, detect the return via focus"
  // handoff. That was dead code — no call site here opened a new tab — and
  // has been deleted from the hook entirely.)
  const { refreshKey } = useReturnFocusRefresh();

  // ACT-1247 — Create/Duplicate/Modify embed is now self-hosted inside the
  // Authorizations module (lib). The shell injects the PP URLs, token, and
  // resolvePpMemberId callback via visibleModulesWithExtras props below.
  // The shell no longer owns embed state or the modal surface.

  // Full-page document detail (PP parity): when a document is opened from the
  // Documents tab, the detail view replaces the entire profile — member
  // header included — instead of rendering inside the tab. Closing it
  // remounts the profile on the Documents tab.
  const [openDocId, setOpenDocId] = useState<number | null>(null);
  const [profileInitialTab, setProfileInitialTab] = useState(INITIAL_TAB);
  const handleDocSelectionChange = (docId: number | null) => {
    if (docId === null) setProfileInitialTab(DOCUMENTS_TAB);
    setOpenDocId(docId);
  };

  // PP members arrive as /members/pp:<companyId>:<mpi>:<mkey>. They have no
  // Pathways record, so we skip MEMBER_BY_ID and build the patient straight
  // from the PP search fields passed via location.state (mirrors
  // providerPortalFrontend's Member360Panel). On a hard refresh
  // location.state is gone — the URL is now the only source, and (AM-5716
  // follow-up) it carries BOTH opaque GUIDs a PP search row has:
  // `membMpiNo` (non-nullable) and `membKeyId`/mkey (nullable — empty
  // segment when absent). It deliberately does NOT carry the real business
  // membId (RmiMembId): care-management-gql's searchProviderPortalMembers
  // `memberId` filter is an exact-equality match on RmiMembId (a varchar
  // business id), and a GUID never matches it — an earlier version of this
  // fix tried re-fetching the PP record via that search keyed on the URL's
  // mpi and always got `[]` back. There is no lookup-by-mpi/mkey endpoint.
  //
  // The correct resolution key for the header is `(companyId && mkey)` —
  // @astrana/ui's usePatientProfile already resolves EZCap/connect-gql
  // patients off that pair (or a real `(memberId && mpi)` pair), so passing
  // `{companyId, mkey, mpi}` straight through is enough for @astrana/ui to
  // render the header itself; no search call needed. `membId` (for
  // Create-Auth / the Duplicate-auth hint, which the URL can't supply) is
  // separately resolved below via the SAME connectPatient point lookup
  // @astrana/ui's own hook performs internally, using mkey as the key — see
  // CONNECT_PATIENT_IDS. No PHI is persisted anywhere in this: URL segments
  // are opaque GUIDs, not names/DOB/etc.
  const isPp = !!id?.startsWith("pp:");
  const locationState = effectiveState;
  const parsedPpId = useMemo(() => parsePpMemberId(id), [id]);
  const urlPpCompanyId = parsedPpId?.companyId ?? "";
  const urlPpMpi = parsedPpId?.mpi ?? "";
  const urlPpMkey = parsedPpId?.mkey;
  // True on a hard refresh / direct link — location.state (and therefore the
  // real membId/fullName/startDate a search row carries) is gone, and
  // everything downstream must be rebuilt from the URL's opaque GUIDs alone.
  const isPpRefresh = isPp && !locationState?.ppMember;
  const ppMember = useMemo<PpMemberState | undefined>(() => {
    if (!id || !isPp) return undefined;
    if (locationState?.ppMember) return locationState.ppMember;
    // Hard refresh / deep link: rebuilt from the URL's opaque GUIDs alone.
    // `healthPlanCode` is intentionally absent here (the URL never carries it —
    // see PpMemberState.healthPlanCode for why it must not). Downstream, that
    // absence is the honest "cannot resolve hpCode" signal the Create Claim
    // entry gate blocks on; nothing substitutes an empty string for it.
    return { companyId: urlPpCompanyId, membMpiNo: urlPpMpi, membKeyId: urlPpMkey };
  }, [id, isPp, locationState?.ppMember, urlPpCompanyId, urlPpMpi, urlPpMkey]);

  // Connect-only fallback members arrive as /members/astrana:<astranaId>. They
  // have no Pathways record, so we skip MEMBER_BY_ID and build the patient
  // straight from the astranaId, letting the library's existing astranaId path
  // (connectPatient(astranaId) via the stitched gateway) render the header +
  // all panels. astranaId comes from location.state (robustness) or, on a hard
  // refresh, from the route id.
  const isAstranaOnly = !!id?.startsWith("astrana:");
  const astranaOnlyId = useMemo<string>(
    () => astranaIdFromNav(id, locationState?.astranaMember),
    [id, locationState?.astranaMember],
  );
  // Optional PP/EZCAP extras a host may carry alongside astranaId (see
  // LocationState.astranaMember's doc above) — undefined for a genuine
  // connect-only match (search/EHR) and on a hard refresh, populated for a
  // worklist-opened member.
  const astranaMemberCompanyId = locationState?.astranaMember?.companyId || undefined;
  const astranaMemberId = locationState?.astranaMember?.memberId || undefined;
  // Worklist-carried name/DOB fallback for the Create-Auth PP crosswalk (see
  // LocationState.astranaMember's doc) — undefined for a genuine connect-only
  // match and on a hard refresh, same as the extras above.
  const astranaMemberName = locationState?.astranaMember?.name || undefined;
  const astranaMemberDob = locationState?.astranaMember?.dob || undefined;

  const ppCompanyId = ppMember?.companyId ?? "";
  const ppMpi = ppMember?.membMpiNo ?? "";
  const ppMkey = ppMember?.membKeyId ?? undefined;
  // Upper-cased once, reused everywhere mkey feeds a connect/EZCap match
  // (the `<MemberProfile>` `patient.mkey` prop below is upper-cased too) —
  // a raw-vs-upper-cased mismatch between the two would make this lookup
  // silently never match even though the header resolves fine.
  const ppMkeyUpper = ppMkey ? ppMkey.toUpperCase() : undefined;

  // AM-5716 follow-up — on the normal (location.state) path `ppMember.membId`
  // is normally already the real RmiMembId from the search row. Fall back to
  // the same connectPatient point lookup @astrana/ui's own usePatientProfile
  // performs internally (keyed by mkey) whenever we have enough to run it
  // AND don't already have a real membId — covers BOTH a hard refresh (URL
  // never carries membId at all, see the PP URL-scheme comment above) AND a
  // normal-path search row whose own membId came back null. Never fall back
  // to mpi here (that mpi-masquerading-as-memberId substitution is the exact
  // bug this ticket fixed for the header; Create-Auth's own
  // stableResolvePpMemberId already fails loud on an empty ppMemberId rather
  // than mint against a wrong id, so it's safe for this to stay "" while
  // loading).
  const shouldResolvePpMemberId = shouldResolvePpMemberIdFallback({
    mkey: ppMkey,
    companyId: ppCompanyId,
    membId: ppMember?.membId,
  });
  const { data: ppConnectIdsData } = useQuery<
    ConnectPatientIdsResponse,
    ConnectPatientIdsVars
  >(CONNECT_PATIENT_IDS, {
    variables: { memberKeyId: ppMkeyUpper, ipaId: ppCompanyId },
    skip: !shouldResolvePpMemberId,
    fetchPolicy: "cache-first",
  });
  const resolvedPpMemberId = selectHealthPlanMemberId(
    ppConnectIdsData,
    ppCompanyId,
  );
  const ppMemberId = ppMember?.membId ?? resolvedPpMemberId ?? "";

  // If a *different* Elation patient chart is detected while viewing this
  // matched patient, offer to switch to the matching flow for them. Sticky:
  // null / no-patient tab changes are ignored (see useEhrPatientSwitch).
  const { candidate: switchCandidate, stay: stayOnPatient } = useEhrPatientSwitch(
    locationState?.ehrPatientId ?? null,
    isActiveTab,
  );

  // ── Visit-note drafting from the open EHR chart (VITE_FEATURE_AI_VISIT_NOTES)
  //
  // The scribe module never reads an EHR itself: the shell adapts its own
  // Elation bridge payload into the EHR-neutral `EhrEncounterSnapshot` and
  // injects the write transport. Both directions are the host's job.
  //
  // `useElationBridge` returns disconnected defaults outside the extension, so
  // in standalone web `elationClinical`/`elationPatient` stay null, the snapshot
  // is null, and the panel simply shows no chart-drafting entry point.
  const {
    data: elationPatient,
    clinical: elationClinical,
  } = useElationBridge();

  const encounterSnapshot = useMemo(
    () =>
      featureFlags.aiVisitNotes
        ? toEhrEncounterSnapshot(elationClinical, elationPatient)
        : null,
    [elationClinical, elationPatient],
  );

  // Chart identity as the HOST sees it right now. Deliberately read from the
  // live bridge payload, NOT from `encounterSnapshot`, so the fail-closed
  // comparison below can never end up comparing a draft against the very
  // snapshot it was derived from (which would make the check vacuous).
  const openChartPatientId = elationPatient?.ehrPatientId ?? undefined;
  const openChartNoteId = elationClinical?.openNote?.noteId ?? undefined;
  // A note being OPEN is observable even when its id is not readable — the
  // scrape's hidden-input selectors do not match the live page. Without this,
  // the write guard refused every write while a note sat open on screen.
  const openChartHasNote = !!elationClinical?.openNote;

  // Write-back needs its own flag on top of the feature flag: drafting is
  // read-only, writing mutates a clinical record.
  const canWriteVisitNoteSections =
    featureFlags.aiVisitNotes && featureFlags.ehrWriteBack;

  // Absent `onWriteSection`, the module renders no write UI at all — it refuses
  // to imply a save it cannot perform. That is the correct flag-off state.
  //
  // Memoised on the chart's PRIMITIVE ids so the handler is rebuilt the moment
  // the open chart changes and can never close over a stale identity.
  const handleWriteVisitNoteSection = useMemo(
    () =>
      canWriteVisitNoteSections
        ? createWriteSectionHandler(
            {
              ehrPatientId: openChartPatientId,
              noteId: openChartNoteId,
              hasOpenNote: openChartHasNote,
            },
            { write: requestElationWrite, refresh: requestElationRefresh },
          )
        : undefined,
    [canWriteVisitNoteSections, openChartPatientId, openChartNoteId, openChartHasNote],
  );

  // AM-5694 (host side) — latest unsaved-changes (dirty) state of the native
  // Create-Auth form, reported by @astrana/ui's `onDirtyChange` (v0.4.39+).
  // A ref (not state) because it's read only inside event handlers/effects
  // below, never rendered — avoids an extra re-render on every keystroke.
  const authFormDirtyRef = useRef(false);
  const stableOnAuthDirtyChange = useRef((isDirty: boolean) => {
    authFormDirtyRef.current = isDirty;
  }).current;

  // AM-5921 — the same contract for the native Create-Claim form. Declared up
  // here beside the auth ref because the guards below close over BOTH; the
  // `onDirtyChange` handler that writes it sits further down with the rest of
  // the claim wiring.
  const claimFormDirtyRef = useRef(false);

  // W1 (visit-note leave/tab-switch guard) — the third dirty surface, same
  // contract as the two above: `VisitNoteDrafter`'s `onDirtyChange`
  // (astrana-ui-modules#583) reports true from the moment a draft exists
  // until "Start over" or unmount. Unlike auth/claim, nothing persists a
  // visit-note draft anywhere yet, so this ref is the ONLY thing standing
  // between a tab switch and silently losing it.
  const noteFormDirtyRef = useRef(false);
  const stableOnNoteDirtyChange = useRef((isDirty: boolean) => {
    noteFormDirtyRef.current = isDirty;
  }).current;

  // AM-5921 — a leave must be blocked when ANY write surface holds unsaved
  // work. Create Claim used to be invisible to every guard here, because they
  // all read `authFormDirtyRef` alone: a fully-entered CMS-1500 was discarded
  // with no prompt on browser Back, "Back to Home", a profile tab change,
  // and — with no user action whatsoever — the EHR patient auto-switch below.
  // Stable identity (the file's `useRef(fn).current` idiom) so the hook and the
  // call sites can hold it without re-subscribing.
  const isAnyFormDirty = useRef(
    () =>
      authFormDirtyRef.current ||
      claimFormDirtyRef.current ||
      noteFormDirtyRef.current,
  ).current;

  // ───────────────────────────────────────────────────────────────────────────
  // THE VISIT NOTE'S OWN LEAVE PROMPT (2026-08-19)
  //
  // Eric, from QA: leaving a dirty note showed "An embedded page at
  // dev-member-360-standalone… says: Are you sure you want to leave this page?
  // Any unsaved changes will be lost." That is the Create-Auth/Create-Claim
  // guard's wording, inherited when PR #362 folded the note into
  // `isAnyFormDirty` — and it is wrong twice over: it is a browser alert where
  // the reviewing physician asked for an in-app choice, and its promise of lost
  // work is false, because the note autosaves a draft.
  //
  // So the note gets @astrana/ui's `LeaveNotePrompt` and the two native write
  // forms keep the native confirm, unchanged. That split is deliberate, not
  // laziness: for Create Auth / Create Claim the browser alert is TRUE (nothing
  // persists a half-entered CMS-1500 anywhere), the note is the surface that
  // regressed, and re-shaping a working data-loss warning as a clinical prompt
  // would be a second, unasked-for change to a shipped AM-5694/AM-5921 flow.
  //
  // WHEN BOTH ARE DIRTY, THE NATIVE CONFIRM WINS. `requestNoteLeave` returns
  // false in that case: real data loss outranks a "you haven't filed this yet"
  // nudge, and stacking both prompts would ask twice for one navigation.
  //
  // Not in scope, and not claimed to be: tab close and refresh. Browsers ignore
  // custom `beforeunload` text — that prompt stays the browser's own, for the
  // note as much as for the forms.
  // ───────────────────────────────────────────────────────────────────────────

  // The navigation that is paused behind the dialog, held as a continuation
  // because a React dialog cannot answer a synchronous guard. State, not a ref:
  // its presence is what renders the dialog.
  const [pendingNoteLeave, setPendingNoteLeave] = useState<
    { proceed: () => void } | null
  >(null);
  // Ref mirror of `pendingNoteLeave`, because `requestNoteLeave` below is a
  // stable closure that cannot see state — and it NEEDS to know whether the
  // dialog is already up. `window.confirm` blocked the event loop, so a second
  // Back could never interleave with an open question; the async dialog opens
  // that window, and an unguarded second request silently REPLACED the stored
  // continuation (PR #363 review, MEDIUM-1). Kept in lockstep with the state:
  // set where the dialog opens, cleared where either answer clears it, and both
  // reset together on the per-member remount (`key={profile.navId}`).
  const pendingNoteLeaveRef = useRef(false);

  // Stable identity (the file's `useRef(fn).current` idiom) so the popstate hook
  // and every call site can hold it without re-subscribing. `setPendingNoteLeave`
  // is itself stable, so closing over it once is safe.
  const requestNoteLeave = useRef((proceed: () => void): boolean => {
    // The scope split AND the re-entrancy rule live in `noteLeavePrompt.ts`,
    // with their reasoning and their own tests — this page is not mountable in
    // a unit test.
    const outcome = decideNoteLeaveRequest(
      {
        auth: authFormDirtyRef.current,
        claim: claimFormDirtyRef.current,
        note: noteFormDirtyRef.current,
      },
      pendingNoteLeaveRef.current,
    );
    if (outcome === "not-ours") return false;
    // The dialog is already on screen asking this very question. Report the
    // leave as handled, but keep the FIRST continuation: the user's single
    // answer must resolve exactly one request, and the popstate hook's
    // re-entrancy contract (see `RequestLeaveDecision`) requires the stored
    // `proceed` to be the one whose question the user actually saw appear.
    if (outcome === "already-asking") return true;
    pendingNoteLeaveRef.current = true;
    // Wrapped in an object: a bare function in state would be mistaken for the
    // updater form of `setState` and called immediately.
    setPendingNoteLeave({ proceed });
    return true;
  }).current;

  /**
   * The one gate for an in-app leave that the host itself performs (the
   * "Back to Home" button and the EHR auto-switch). `proceed` runs now if
   * nothing objects, later if the note's dialog is answered with "leave", and
   * never if either prompt is declined.
   */
  const guardedLeave = (proceed: () => void) => {
    if (requestNoteLeave(proceed)) return;
    if (!confirmLeaveIfDirty()) return;
    proceed();
  };

  // Browser Back/Forward guard while either form is dirty. Tab-close/refresh is
  // already covered by each module's own self-attached `beforeunload` guard
  // (CreateAuthForm AM-5694, CreateClaimForm AM-5921, VisitNoteDrafter W1) — see
  // useUnsavedChangesGuard.ts's header for the full scope split.
  useUnsavedChangesGuard(isAnyFormDirty, navigate, requestNoteLeave);

  // In-app navigation away from the profile (header/"Back to Home" and the
  // EHR patient-switch auto-nav) doesn't fire `popstate` (it's a `navigate()`
  // push, not a browser Back) — gate those call sites with this instead.
  // Returns true when it's safe to proceed. NOT used for SwitchPatientModal's
  // Navigate action (AM-5706): that modal is its own confirmation affordance,
  // so a `window.confirm` on top would double-prompt — see its render below.
  const confirmLeaveIfDirty = (): boolean =>
    !isAnyFormDirty() || window.confirm(UNSAVED_CHANGES_CONFIRM_MESSAGE);

  // Default behavior: auto-navigate straight into the new patient's matching
  // flow with NO confirmation modal. navigate("/members") returns to Member
  // Search, where the EHR context now carries the new patient and the existing
  // auto-nav effects take it the rest of the way to the new member's profile.
  // On the classic route, navigating away unmounts this page (and
  // useEhrPatientSwitch), so it can't re-fire; MemberSearchPage's per-patient
  // guard prevents any A→B→A bounce. Under the keep-alive tab shell this page
  // does NOT unmount on navigate — but `switchCandidate` is null unless this is
  // the active tab (useEhrPatientSwitch's `isActive` gate), so a hidden tab
  // never fires this, and the now-active Search tab's guard still blocks the
  // bounce. The confirmation modal is opt-in via VITE_FEATURE_EHR_SWITCH_CONFIRM.
  //
  // resetAutoNav() first: the switch target may be the SAME in-context patient
  // whose id still sits in the module-level auto-nav guard (e.g. you auto-navved
  // to A, manually opened an unrelated member, then re-focused A's chart). Then
  // `hasAutoNavigated(A)` is still true and MemberSearchPage's auto-nav would
  // NOT re-fire — leaving you stranded on Search. Clearing the guard lets it
  // re-navigate to whatever the foreground chart now shows. (For a genuinely
  // different patient, syncInContextPatient already clears it; this covers the
  // same-patient re-focus case syncInContextPatient treats as a no-op.)
  useEffect(() => {
    if (!shouldAutoNavigateOnSwitch(featureFlags.ehrSwitchConfirm, switchCandidate))
      return;
    // Felicia's ask covers this path by name: "if you click out of the note or
    // change patient chart, you should have a pop up". This is the change-chart
    // one, and it is the only leave here with NO user action behind it at all —
    // the clinician opened another chart in Elation, not this app.
    guardedLeave(() => {
      resetAutoNav();
      navigate("/members");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switchCandidate, navigate]);

  // AM-5412 / ACT-1247 — Duplicate/Modify auth and Create auth are now
  // self-hosted inside the Authorizations lib module via the embed props
  // injected in visibleModulesWithExtras below. The shell-side
  // handleDuplicateAuthRef / onDuplicateAuth / handleCreateAuthRef wrappers
  // have been removed; the lib owns the embed panel state machine.


  // ACT-1303 — filter/order the Home tab's 6 cards per the active persona's
  // server-driven `raw.currentPersona.modules[]` config. Independent of the
  // whole-tab gate below (which only decides whether "Home" is shown at all).
  //
  // Cast: HOME_CARDS_DEFAULT is typed as `ModuleGroup["modules"]`
  // (`(ModuleObject | ReactNode)[]`, matching the shape MemberProfile's
  // ModuleGroup.modules expects) so its CASES entry's `props.actions` narrows
  // correctly against the discriminated ModuleType union at its declaration
  // site — annotating it as the narrower `ModuleObject[]` there defeats that
  // narrowing (TS then checks each literal top-down against the full union
  // and misreports CASES's props as excess). useVisibleHomeCards only reads
  // `.type` off ModuleObject entries, and every Home card here is a plain
  // `{ type, props? }` object, never a bare ReactNode — so the cast is safe.
  const homeCards = useVisibleHomeCards(HOME_CARDS_DEFAULT as ModuleObject[]);

  // ACT-1488 (T9) — the Clinical Summary card header. This is UNDEFINED BY
  // DEFAULT, which is the parity behaviour, not a fallback: with
  // `personaClinicalSummaryTitle` off (the default) no `title` prop is passed
  // and @astrana/ui renders its own literal "Clinical Summary", matching
  // Atlas/PP for every persona instead of the PROVIDER/ADMIN template name
  // 'Pre-Round Summary'. Flip VITE_FEATURE_PERSONA_CLINICAL_SUMMARY_TITLE to
  // restore the per-persona header here AND in PracticeSchedulePage, which
  // shares this resolver so the two can never disagree. Sourced once from the
  // already-loaded current persona (not per-member); still undefined when
  // VITE_FEATURE_CLINICAL_SUMMARY_TEMPLATE_TITLE omits the field from the
  // CurrentUser query, the backend returned no template, or the persona has
  // none. See `clinicalSummaryTitle.ts`.
  const clinicalSummaryTitle = clinicalSummaryTitleOverride(
    currentUserRaw?.currentPersona?.clinicalSummaryTemplate?.title,
    featureFlags.personaClinicalSummaryTitle,
  );
  // T6 — the ClinicalSummary card's "BP Recordings" section is gated on the
  // EHR-chart condition ALONE: a chart open in the CoPilot sidecar with a visit
  // note open in it. It is a SECTION INSIDE the ClinicalSummary module, not a
  // module of its own, so it is deliberately NOT persona-gated — see
  // `isBpRecordingsVisible` for that and for why `clinical.openNote` and not
  // `status`/`sourceTabActive`.
  //
  // BEHAVIOUR CHANGE: `useElationBridge` returns disconnected defaults outside
  // the extension, so `elationClinical` is always null in the standalone
  // desktop web app — BP Recordings is HIDDEN there, and appears only in the
  // CoPilot side panel with a chart + open note. Injected as the
  // `showBloodPressure` prop alongside the title below; `false` makes
  // @astrana/ui hide the section AND skip the BP history fetch.
  const showBloodPressure = isBpRecordingsVisible(elationClinical);
  const homeCardsWithTitle = useMemo<ModuleObject[]>(
    () =>
      homeCards.map((card) =>
        "type" in card && card.type === ModuleType.CLINICAL_SUMMARY
          ? {
              ...card,
              props: {
                ...(card.props ?? {}),
                ...(clinicalSummaryTitle
                  ? { title: clinicalSummaryTitle }
                  : {}),
                showBloodPressure,
              },
            }
          : card,
      ),
    [homeCards, clinicalSummaryTitle, showBloodPressure],
  );

  // ACT-1303-style — Demographics is now an ADMIN-CONFIGURABLE Home card, like
  // the other 6: it flows through `useVisibleHomeCards`'s persona filter above
  // (via its `HOME_CARDS_DEFAULT` entry) instead of being spliced in as an
  // always-on special case. Its default EXPANDED state is carried in the
  // "HomeDemographics" module's per-persona `actions[]` array (no backend
  // schema change) — a `"collapsedByDefault"` flag flips the card to start
  // collapsed; its absence (the common case) defaults to expanded.
  const homeDemographicsActions = useModuleActions("HomeDemographics");
  const demographicsDefaultExpanded = !homeDemographicsActions.includes("collapsedByDefault");

  // Care Team's default open state, same free-text `actions[]` mechanism as
  // HomeDemographics above (`member_360.persona_modules`, read via
  // useModuleActions — no backend/schema/@astrana/ui change). The POLARITY is
  // deliberately INVERTED versus Demographics: the product directive is an
  // "auto-collapsed row", so the default with zero seeding is COLLAPSED, and an
  // admin adds "expandedByDefault" to a persona's "HomeCareTeam" actions to make
  // that persona start it open. Deliberately NOT registered in
  // featureCatalog.ts's HomeCareTeam entry: catalogActions() is the fallback for
  // the admin-role bypass and the no-persona seam, so registering it there would
  // make admins and unprovisioned users start EXPANDED — the opposite of the
  // directive. Unregistered means those paths return [] → collapsed, as intended.
  const homeCareTeamActions = useModuleActions("HomeCareTeam");
  const careTeamDefaultExpanded =
    homeCareTeamActions.includes("expandedByDefault");

  // ACT-1342 (2026-07-29 directive) — per-persona OPT-OUT of the interactive
  // Quality Gaps checklist, mirroring the HomeDemographics
  // collapsedByDefault pattern immediately above: same free-text
  // `actions[]` mechanism (`member_360.persona_modules`), same
  // `useModuleActions(moduleId)` reader, no backend/schema/GraphQL/
  // `@astrana/ui` change. Deliberately OPT-OUT (default ON, zero seeding
  // needed to demo): an admin adds `"qualityGapsReadOnly"` to a persona's
  // "HomeQualityGaps" actions to revert THAT persona to the old read-only
  // card. This is NOT the deleted `VITE_FEATURE_QUALITY_GAP_CHECKLIST`
  // build-time env flag (that stays gone) — it's admin-configurable,
  // per-persona PRODUCT config, the same category as
  // `demographicsDefaultExpanded` above.
  //
  // DELIBERATELY NOT registered in featureCatalog.ts's HomeQualityGaps
  // entry: `catalogActions(moduleId)` is the fallback actions list for the
  // admin-role bypass AND the no-persona/enforcement-off transition seam
  // (see useFeatureAccess.ts's `resolveModuleActions`, cases 0 and 1) — if
  // `"qualityGapsReadOnly"` were registered there, admins and unprovisioned
  // users would inherit the DEGRADED card, the exact opposite of the
  // opt-out's intent. Leaving it unregistered means those two paths return
  // `[]` (no explicit restriction), which reads as "checklist ACTIVE" below
  // — the desired default for every persona that hasn't explicitly opted out.
  const homeQualityGapsActions = useModuleActions("HomeQualityGaps");
  const qualityGapsReadOnlyForPersona = homeQualityGapsActions.includes(
    "qualityGapsReadOnly",
  );

  // Inject the resolved `defaultExpanded` onto the Demographics card entry,
  // mirroring the clinical-summary title injection above (map over the
  // already-filtered/ordered list rather than re-running useVisibleHomeCards).
  const homeCardsFinal = useMemo<ModuleObject[]>(
    () =>
      homeCardsWithTitle.map((card) => {
        if (!("type" in card)) return card;
        if (card.type === ModuleType.DEMOGRAPHICS) {
          return {
            ...card,
            props: { ...(card.props ?? {}), defaultExpanded: demographicsDefaultExpanded },
          };
        }
        if (card.type === ModuleType.CARE_TEAM) {
          return {
            ...card,
            props: { ...(card.props ?? {}), defaultExpanded: careTeamDefaultExpanded },
          };
        }
        return card;
      }),
    [homeCardsWithTitle, demographicsDefaultExpanded, careTeamDefaultExpanded],
  );

  // Rebuild the master ordered list with the persona-filtered, title- and
  // defaultExpanded-mapped Home cards spliced in. Memoized on `homeCardsFinal`
  // (itself memoized/stable when unchanged) so this doesn't defeat
  // useVisibleModules's own memo below.
  const allModules = useMemo<ModuleGroup[]>(
    () => [{ tabName: "Home", modules: homeCardsFinal }, ...OTHER_MODULES],
    [homeCardsFinal],
  );

  // AC-ADM-3: `useVisibleModules` is NO LONGER applied here. It moved to AFTER
  // the extras merge (`visibleModulesWithExtras` below) so that the flag-gated
  // extras — Notes, Scribe, Documents — pass through the very same persona gate
  // as the statically-declared tabs instead of being appended past it. Filtering
  // twice would be harmless but pointless; filtering ONCE, last, is what makes
  // "every tab is persona-gated" true by construction rather than by convention.
  //
  // initialTab is computed further below (after visibleModulesWithExtras and
  // the tab-order sort), so the admin-configured landing tab / first-visible-
  // tab fallback both follow the FINAL sorted, extras-merged tab list.

  // Persona WRITE gate for Authorizations. useVisibleModules already hides the
  // whole Auths TAB when the persona lacks the "Auths" module; this is the finer
  // per-ACTION gate: even with the tab visible, the Create-auth + Duplicate/Modify
  // write affordances only appear when the persona grants the "create" action.
  // The lib renders these solely when ppWebBaseUrl/accessToken props are present,
  // so withholding them (see below) hides all write controls with no @astrana/ui
  // change. A persona with actions:["view"] (or no actions listed) sees Auths read-only.
  const personaAllowsCreateAuth = useActionAllowed("Auths", "create");
  // DEMO BYPASS (ACT-1247 — deliberately reversible): users who signed in
  // through Provider Portal (selectedProvider === "provider-portal-b2c") get
  // Create Auth regardless of the Auths "create" persona action, so PP-login
  // demos don't require persona setup. Non-PP sign-ins (Entra / Pathways)
  // still fall through to the normal persona gate — fail-closed for them.
  // Remove this OR-clause to restore strict persona gating for everyone.
  const canCreateAuth =
    personaAllowsCreateAuth || selectedProvider === "provider-portal-b2c";

  // ACT-1253 — persona WRITE gate for Outreach. The whole-tab gate
  // (useVisibleModules) already hides the Outreach tab when the persona lacks
  // the module; this finer per-ACTION gate governs the create/log + dispatch
  // affordance inside the tab. Withheld → the lib renders no create surface
  // (canCreate prop below). No PP demo-bypass here — strict persona gate.
  const canCreateOutreach = useActionAllowed("Outreach", "create");

  // Per-claim "Print EOB" CTA (Claims detail view). Gated by the Claims
  // "printEob" action — grant it to CUSTOMER_SERVICE server-side to expose the
  // button for that persona (admins get it via the role all-access path).
  const canPrintEob = useActionAllowed("Claims", "printEob");

  // ── Native Create Claim (feat/create-claim-native-host) ─────────────────────
  // Persona WRITE gate for Claims. The featureCatalog defines Claims with
  // actions: ["view", "submit"] — "submit" is the write gate (matches the
  // legacy "Submit Claim" button contract). useVisibleModules already hides
  // the whole Claims TAB when the persona lacks the "Claims" module; this gate
  // additionally controls whether the native CreateClaim panel is offered.
  // Net-new surface — gated purely on the persona action, no feature flag.
  const canCreateClaim = useActionAllowed("Claims", "submit");

  // Open/close state for the CreateClaim panel within the Claims tab.
  const [createClaimOpen, setCreateClaimOpen] = useState(false);

  // ── Create Claim ENTRY GATE (port of PP's CreateClaimModal gate chain) ──
  // `claimGateOpen` is what the "Create Claim" button now opens; the form
  // itself opens only after the gate hands back a provider. `claimProvider` is
  // that provider — a REAL claim-write grant (GUID `RmiProvKeyId`) from the
  // acting IPA. Without it the module would send `provKeyId=""`, which PP-API
  // answers with an HTTP 400. The gate is the only thing gating an OPEN, seeded
  // form (the seed endpoint checks no entitlement); PP-API's `SaveClaim` does
  // re-authorize the billing provider before filing, so this is not the last line
  // of defense — see claims/claimProviderList.ts's header for both halves.
  const [claimGateOpen, setClaimGateOpen] = useState(false);
  const [claimProvider, setClaimProvider] = useState<ClaimProvider | null>(null);
  // The member half of the gate's decision: ONE Provider Portal member row's
  // `membKeyId` + `hpCode` + `startDate`, resolved and validated by the gate (see
  // claims/usePpClaimMemberSeed.ts) and handed back through `onProceed`. Stored
  // as the seed rather than re-derived per field because PP-API's claim shell
  // proc matches the member on those three as a COMPOSITE key and INSERTs even
  // when it misses — a trio assembled from different sources yields an HTTP 200
  // with a blank-patient claim. Set only by `onProceed`, alongside the provider.
  const [claimSeed, setClaimSeed] = useState<PpClaimMemberSeed | null>(null);
  // The gate DECISION `claimProvider` was resolved under — a `claimGateKey`, i.e.
  // the (memberId, hpCode, companyId) triple, NOT just the member id. In shell
  // mode each patient tab is its own instance (`key={tab.navId}`), so this state
  // is already per-member — but the CLASSIC route (`App.tsx`'s `/members/:id`)
  // mounts this page unkeyed, and React Router reuses the instance across `:id`
  // changes. A switch-patient there would otherwise keep an open Create Claim
  // form and its provider while the member underneath changed, which means the
  // NEW member's health-plan block rules (Caremore/NCPN, HNCC, BSPP) never ran —
  // those are contractual payer-routing rules, so silently skipping them is not
  // acceptable. The full triple is keyed rather than the member alone because a
  // refetch (any cache-and-network revalidation; AM-6260 removed the
  // `refreshNow()` one) can return a DIFFERENT healthplanCode or ipaCode under the same
  // memberPolicyId, which changes the rules' answer without changing the member.
  // Pairing the provider with the whole decision turns every such drift into a
  // fail-closed no-provider state, which the open condition already blocks on.
  const [claimProviderFor, setClaimProviderFor] = useState<string | null>(null);

  // DEMO-MOCK ISOLATION: the PP-API fetch mock (dev/mockPpApi) intercepts ONLY
  // while the native Create Claim form is open, so its claim demo data can never
  // shadow the real `AuthMember/*` lookups the Create AUTH form shares. Set the
  // flag SYNCHRONOUSLY during render (not a useEffect) so it's true before the
  // CreateClaim child mounts and fires its first lookup — child effects run
  // before parent effects, so a useEffect here would race that first fetch.
  //
  // Deliberately NOT widened to `claimGateOpen`: the entry gate's provider-list
  // endpoint is NOT mocked, by design. It is the Create Claim authorization gate,
  // and a fixture that answers "yes" has no business running in any environment.
  // Until that endpoint deploys the gate fails closed and Create Claim is
  // correctly unavailable — the intended interim state, not something to fake.
  // TEMPORARY: remove alongside the demo mock when SearchCPTCodeByTypes lands.
  if (typeof window !== "undefined") {
    (window as unknown as { __ppClaimsDemoActive?: boolean }).__ppClaimsDemoActive =
      canCreateClaim && createClaimOpen;
  }
  // createClaimAction: "Create" (new claim) or "Duplicate" (pre-fill from claimId).
  // setCreateClaimAction is exposed for a future trigger button / row-action
  // handler; prefixed _ to suppress unused-value warning until that lands.
  const [createClaimAction, _setCreateClaimAction] = useState<"Create" | "Duplicate">("Create");

  // isReadOnly: forward the host's peek/impersonation state to the module so
  // it can disable Submit during "view as" sessions.
  // TODO(create-claim P6): member-360-standalone does not yet expose a
  // peek/impersonation signal for Claims. Using `false` (never read-only) is a
  // TEMPORARY placeholder — it means the module fails OPEN on submit if peek
  // is ever enabled on this code path.
  // SHIP-GATE: resolve before enabling native Create Claim in any environment
  // where impersonation/peek is possible.
  const isClaimReadOnly: boolean = false;
  const claimReadOnlyReason: string | undefined = undefined;

  // NOTE: Create Claim's `getAccessToken` (`stableGetClaimAccessToken`) is
  // defined further down, immediately after `ensurePpSession` — it awaits that
  // to re-mint on expiry, so it cannot be declared before it. See there for the
  // full rationale on why the bearer is the PP-B2C token and not the ambient one.

  // userRole: from useCurrentUser()'s resolved role string (e.g. "PROVIDER").
  const claimUserRole = currentUserRole ?? undefined;

  // ── Stable claim callbacks ──────────────────────────────────────────────
  // AM-6260 — deliberately a NO-OP, for the same reason `stableOnAuthCreated`
  // below is one (see its comment for the full rationale). `onClaimCreated`
  // fires the moment the SaveClaim succeeds, while the module's own
  // ClaimConfirmationPage is still mounted and waiting on the user to click
  // Done — it is REFRESH-ONLY and does NOT close the module (@astrana/ui
  // CreateClaim.tsx: "onClaimCreated ... does NOT close the module;
  // ClaimConfirmationPage's 'Done' does that"). This handler used to call
  // `refreshNow()`, which bumps `refreshKey` and — because `refreshKey` feeds
  // the `key` on the <GraphQLClientGate> wrapping the WHOLE <MemberProfile>
  // subtree below — forces an immediate remount of that entire subtree. That
  // destroyed the confirmation screen's local React state before the user ever
  // saw it and reset the active tab back to Home, which is exactly the reported
  // bug: "user is redirected to Member360 Home instead of the Claim Submission
  // page, with no success message".
  //
  // Nothing is lost by not refreshing. The Claims tab list is
  // `connectPatient.claims` from care-management-gql (the EZCap claims
  // warehouse — see @astrana/ui's `useMedicalClaims` / MEDICAL_CLAIMS_QUERY),
  // NOT the PP-API temp claim that was just submitted, so a freshly created
  // claim does not appear there on a refetch regardless. And leaving the Claims
  // tab and returning remounts the module anyway (MemberProfile only renders
  // the active tab), which refetches on its own.
  //
  // (AM-5828 was this handler also arming the now-deleted `markHandoffPending`
  // flag — see the handoff-purge note above `useReturnFocusRefresh()`.)
  const stableOnClaimCreated = useRef(() => {}).current;

  const stableOnClaimClose = useRef(() => {
    setCreateClaimOpen(false);
    // Drop the resolved provider AND member seed too: the next Create Claim must
    // re-run the entry gate (grants, health-plan rules, the member's PP row and
    // the provider choice are all re-verified), never silently reuse a stale one.
    setClaimProvider(null);
    setClaimProviderFor(null);
    setClaimSeed(null);
  }).current;

  const stableOnClaimError = useRef((msg: string) => {
    toast.error(msg);
  }).current;

  // onDirtyChange: feeds the leave-page guards, exactly as the Auths one above
  // does (AM-5921 — closes the `TODO(create-claim P6)` that left this signal
  // tracked but never read, so Create Claim had no exit protection at all).
  // A ref rather than state, matching `authFormDirtyRef`: it's read only from
  // event handlers, never rendered, so state would re-render the whole profile
  // on the first keystroke of every field group for nothing. The module clears
  // it on unmount (CreateClaimForm's `onDirtyChange?.(false)` cleanup), so a
  // closed form can never leave the guard armed.
  const stableOnClaimDirtyChange = useRef((dirty: boolean) => {
    claimFormDirtyRef.current = dirty;
  }).current;

  // onAnalyticsEvent: TODO(create-claim P6) — wire to App Insights' trackEvent
  // once the analytics contract for claim events is defined. Console-logs in
  // dev only for now.
  const stableOnClaimAnalytics = useRef(
    (
      event: "CLAIM CREATION" | "CLAIMS SUCCESS" | "CLAIMS FAILURE",
      meta?: Record<string, unknown>,
    ) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.info("[create-claim] analytics:", event, meta);
      }
    },
  ).current;
  // ── End Native Create Claim ──────────────────────────────────────────────

  // Create-Auth seamless PP login (ACT-1247 follow-up). `identityEmail` is
  // the email the user is CURRENTLY signed in as (Entra/Pathways/PP) — computed
  // FIRST because `getPpToken` requires it to verify a stashed PP token
  // actually belongs to this identity (never trust a foreign/stale stash).
  // `ppToken` is the ALREADY-active, identity-verified PP bearer (own session
  // if signed in via provider-portal-b2c, else a side-by-side stashed token
  // from a prior PpLoginModal run) — see auth/ppToken.ts.
  const identityEmail = resolveIdentityEmail(accountLabel);
  const ppToken = getPpToken(selectedProvider, primarySessionToken, identityEmail);
  const ppEligible = canCreateAuth && isPpB2cConfigured && !!identityEmail;
  // READ-scope eligibility: the same check as `ppEligible` MINUS `canCreateAuth`.
  // Print / Symptoms on the auth detail is a read-only PP API call
  // (AuthMember/GetAuthDetailsPrintPdf), so "can this identity ever hold a PP
  // bearer?" must not be answered with a CREATE permission. A persona without
  // Auths:create, or a linked account the create pre-flight rejected, still
  // gets to print. Used for the `ppRead*` props handed to @astrana/ui below.
  const ppReadEligible = isPpB2cConfigured && !!identityEmail;
  // Same underlying check `usePpCreateAuthPreflight` exposes as `hasLink`
  // (`hasPersistedPpLink`, a pure/cheap localStorage read) — computed
  // directly here too because `identityToken` (below) needs it BEFORE
  // `ppPreflight` exists (ppPreflight's own `userId`/`userRole` args are
  // DERIVED from `identityToken`, so `identityToken` can't wait on the hook).
  // Deterministic pure function of the same `identityEmail` within the same
  // render pass as `ppPreflight.hasLink` — guaranteed to agree with it, never
  // a second, divergent source of truth.
  const identityHasPpLink = hasPersistedPpLink(identityEmail);
  ppSessionCtxRef.current = {
    selectedProvider,
    sessionAccessToken: primarySessionToken,
    identityEmail,
  };

  // ACT-1247 — stable ref carrying the live per-render embed context.
  // Updated on every render (after loadedMember / companyId / handoffMemberIdHint
  // are known, below the early-return guards) so callbacks passed into the stable
  // memo wrappers below always close over fresh values — mirrors the
  // handleDuplicateAuthRef pattern.
  const embedCtxRef = useRef({
    companyId: undefined as string | undefined,
    memberIdHint: "" as string,
    isPp: false,
    ppCompanyId: "",
    ppMemberId: "",
    loadedMember: null as {
      name?: string | null;
      dob?: string | null;
      ipaCode?: string | null;
      memberKeyId?: string | null;
    } | null,
    apolloClient: null as Parameters<typeof resolvePpMembId>[0] | null,
    // Worklist-carried name/DOB fallback for an astrana:-only member (see
    // LocationState.astranaMember's doc) — undefined for a genuine
    // connect-only match, where loadedMember is also null and the crosswalk
    // is expected to fail loud.
    astranaMemberName: undefined as string | undefined,
    astranaMemberDob: undefined as string | undefined,
  });

  // Create-Auth's async "get a real PP bearer" seam. If the user already has
  // a valid, identity-verified PP session, resolves immediately (no-op) —
  // covers provider-portal-b2c primary users AND SSO/Pathways users who
  // already ran PpLoginModal earlier this session (as THEMSELVES). Otherwise
  // delegates to `ppLoginGateRef` (idempotent — see auth/ppLoginGate.ts),
  // which opens PpLoginModal (locked to the current identity email) and
  // suspends until the user submits or cancels. On submit success,
  // `handlePpLoginSuccess` uses `flushSync` to force this component to
  // re-render with the real PP token BEFORE this promise resolves — so by
  // the time the caller (stableResolvePpMemberId's Create/Duplicate path, the
  // "Link Provider Portal" banner below, OR the onError-triggered retry-
  // prompt for Modify) continues past the `await`, the module's live
  // accessToken ref is already the real token.
  const ensurePpSession = useRef((): Promise<void> => {
    const ctx = ppSessionCtxRef.current;
    if (getPpToken(ctx.selectedProvider, ctx.sessionAccessToken, ctx.identityEmail)) {
      return Promise.resolve();
    }
    if (!ctx.identityEmail) {
      // Unreachable in practice: every caller of ensurePpSession (the
      // module's own resolvePpMemberId once a real token already made
      // canSelfHostEmbed() true, the "Link Provider Portal" banner, and the
      // Modify mint-failure retry) is itself gated on a truthy identityEmail.
      // Fail loud rather than opening a modal with no email.
      return Promise.reject(
        new Error("No signed-in identity email available for Provider Portal sign-in."),
      );
    }
    // ACT-1247 SSO-PP auth-linking Phase 1 (Option A) — before prompting,
    // try a SILENT mint off the persisted PP refresh token bound to this
    // identity (`ensurePpAccessToken` never opens UI). Only fall back to
    // PpLoginModal if there's no link at all or the refresh grant itself
    // fails.
    return ensurePpAccessToken(ctx.identityEmail).then((token) => {
      if (token) {
        // Same ordering guarantee as handlePpLoginSuccess below: force a
        // synchronous re-render BEFORE resolving so resolvedAccessToken /
        // @astrana/ui's live accessToken ref pick up the refreshed token
        // before the caller continues past this await.
        flushSync(() => setPpTokenRefreshTick((t) => t + 1));
        return;
      }
      return ppLoginGateRef.current!.request();
    });
  }).current;

  const handlePpLoginSuccess = useRef(() => {
    // Synchronous re-render BEFORE resolving — this is the ordering
    // guarantee a real, working PP bearer depends on reaching the module
    // before any query fires off it. flushSync forces MemberProfilePage's
    // render (and therefore the recomputed `resolvedAccessToken` /
    // `visibleModulesWithExtras` prop values) to commit down into
    // @astrana/ui's live accessToken ref before `gate.succeed()` lets
    // ensurePpSession's awaiter(s) continue.
    flushSync(() => setPpLoginOpen(false));
    ppLoginGateRef.current?.succeed();
  }).current;

  const handlePpLoginCancel = useRef(() => {
    setPpLoginOpen(false);
    ppLoginGateRef.current?.cancel();
  }).current;

  // Link-then-print seam handed to @astrana/ui as `ensurePpReadSession`. Same
  // `ensurePpSession()` gate the native Create/Duplicate path uses via
  // `stableResolvePpMemberId` (silent mint off the persisted refresh token
  // first, PpLoginModal only if that fails), but it RETURNS THE TOKEN rather
  // than relying on a prop refresh: the module publishes the auth-detail action
  // bar imperatively into the shell's state, so its props are frozen at publish
  // time and a re-render alone would not reach the already-mounted Print
  // button. Reads the freshly-minted bearer out of the live ctx ref, which
  // `ensurePpSession` flushSync-commits BEFORE resolving. Rejects on cancel
  // with PP_LOGIN_CANCELLED_MESSAGE, which the module's `onError` below
  // already swallows.
  const stableEnsurePpReadSession = useRef((): Promise<string> => {
    return ensurePpSession().then(() => {
      const ctx = ppSessionCtxRef.current;
      const minted = getPpToken(
        ctx.selectedProvider,
        ctx.sessionAccessToken,
        ctx.identityEmail,
      );
      if (!minted) {
        throw new Error(
          "Provider Portal sign-in did not produce an access token.",
        );
      }
      return minted;
    });
  }).current;

  // AM-5619 — `getAccessToken` for CreateClaimConfig.
  //
  // The bearer is the PP-B2C token, NOT the ambient primary session token.
  // Create Claim's calls are real `ClaimMember/*` / `AuthMember/*` requests
  // against PP-API behind APIM, and APIM rejects a non-PP bearer with a 401
  // before the request ever reaches the .NET app (which registers no auth
  // scheme of its own — `Program.cs:211`'s `UseAuthorization()` has nothing
  // configured behind it). The old ambient-token wiring was correct ONLY while
  // every Claim endpoint was served by the local demo mock.
  //
  // This is ASYNC, which is why it lives here rather than above: the module's
  // `CreateClaimConfig.getAccessToken` is typed `() => string | Promise<string>`
  // and its baseQuery `await`s it, so a form left open past B2C expiry can
  // refresh instead of 401ing on submit.
  //
  // ⚠️ SILENT REFRESH ONLY — this must NEVER open PpLoginModal.
  // It is called from inside `prepareHeaders`, on EVERY request, including
  // per-keystroke lazy typeaheads (diag search, service codes, VerifyNPI).
  // Driving interactive UI from there is structurally wrong and was tried:
  //   - `PpLoginGate.cancel()` only rejects the CURRENT cycle and nulls
  //     `pending`, so there is no "user declined" memory — the very next
  //     keystroke reopens the modal. The user cannot dismiss it without
  //     abandoning the form.
  //   - The cancel path is racy: a concurrent request that calls `request()`
  //     after the `.finally` microtask starts a FRESH cycle whose
  //     `setPpLoginOpen(true)` batches after the cancel's `setPpLoginOpen(false)`,
  //     leaving the just-cancelled modal on screen.
  // Interactive re-auth belongs on a USER GESTURE — that is the "Create Claim"
  // onClick gate below, which is the right seam for it.
  //
  // `ensurePpAccessToken` is non-interactive by contract (it only replays the
  // persisted refresh grant), so it is safe here. On failure return "" and let
  // the request 401 honestly; throwing would surface as an opaque RTK Query
  // transport error instead.
  //
  // Identity is stable (useRef.current), so it's safe as a memo dep / prop.
  // Reads the LIVE ctx ref, never a captured render value.
  const claimTokenDisposedRef = useRef(false);
  /** Guards the Create Claim gate against double-clicks during a silent mint. */
  const claimGatePendingRef = useRef(false);
  const stableGetClaimAccessToken = useRef(async (): Promise<string> => {
    // The page can unmount (logout redirect / nav) while a refresh is parked.
    // Without this the resumed closure would re-read the ctx ref and hand a
    // still-live bearer to a request fired after logout.
    if (claimTokenDisposedRef.current) return "";
    const ctx = ppSessionCtxRef.current;
    const existing = getPpToken(
      ctx.selectedProvider,
      ctx.sessionAccessToken,
      ctx.identityEmail,
    );
    if (existing) return existing;
    // Say WHY getPpToken fail-closed. It has several independent early-outs
    // (wrong provider, no bound identity, no stash, identity mismatch,
    // undecodable/expired token) and they collapse into one empty string, so a
    // PP-signed-in user who is refused here has no way to tell which happened.
    // No token values or email addresses are logged — shape only.
    if (import.meta.env.DEV) {
      const claims = ctx.sessionAccessToken ? decodeJwtClaims(ctx.sessionAccessToken) : null;
      const exp = (claims as { exp?: number } | null)?.exp;
      console.error("[claims] getPpToken returned nothing. Shape:", {
        selectedProvider: ctx.selectedProvider,
        hasSessionAccessToken: !!ctx.sessionAccessToken,
        sessionTokenLooksLikeJwt: (ctx.sessionAccessToken ?? "").split(".").length === 3,
        sessionTokenDecodes: !!claims,
        expClaimPresent: exp !== undefined,
        expInFuture: exp !== undefined ? exp * 1000 > Date.now() : undefined,
        hasIdentityEmail: !!ctx.identityEmail,
        isPpB2cConfigured,
        hasPersistedLink: hasPersistedPpLink(ctx.identityEmail),
      });
    }
    // No usable bearer (absent, or getPpToken fail-closed it as expired).
    if (!ctx.identityEmail) return "";
    try {
      await ensurePpAccessToken(ctx.identityEmail);
    } catch {
      return "";
    }
    if (claimTokenDisposedRef.current) return "";
    const ctxAfter = ppSessionCtxRef.current;
    return (
      getPpToken(
        ctxAfter.selectedProvider,
        ctxAfter.sessionAccessToken,
        ctxAfter.identityEmail,
      ) ?? ""
    );
  }).current;

  // Trips on unmount so any parked refresh above resolves to "" instead of
  // emitting a bearer post-logout, and releases a gate promise nobody will
  // ever settle. (This is the safety net the deleted claimBearerRef cleanup
  // used to provide.)
  //
  // The setup body RE-ARMING this to false is load-bearing, not defensive
  // tidiness. `claimTokenDisposedRef` lives in a `useRef`, so it survives the
  // effect being torn down and set up again on the SAME mount — which is
  // exactly what `<React.StrictMode>` (main.tsx:77) does in development:
  // mount -> run effect -> run cleanup -> run effect again. Without this line
  // the cleanup latched the flag `true` on every dev mount, and
  // `stableGetClaimAccessToken`'s first statement (`if
  // (claimTokenDisposedRef.current) return ""`) then returned an empty bearer
  // for the rest of the page's life. Create Claim was therefore hard-blocked
  // with "Provider Portal sign-in required" on localhost for every user —
  // including one holding a perfectly valid, unexpired PP token — while
  // production, which does not double-invoke effects, worked fine. A
  // dev-only-but-total outage of the feature, and invisible to QA.
  useEffect(() => {
    claimTokenDisposedRef.current = false;
    return () => {
      claimTokenDisposedRef.current = true;
      ppLoginGateRef.current?.cancel();
    };
  }, []);

  // Stable wrappers read from the ref — never change identity, so safe as
  // memo deps / prop values.
  const stableResolvePpMemberId = useRef(async (): Promise<string> => {
    await ensurePpSession();
    const ctx = embedCtxRef.current;
    if (ctx.isPp) {
      if (!ctx.ppMemberId) {
        throw new PpMemberResolutionError({
          name: undefined,
          dob: undefined,
          ipaCode: ctx.ppCompanyId,
        });
      }
      return ctx.ppMemberId;
    }
    if (!ctx.apolloClient) {
      throw new Error("Apollo client not yet available.");
    }
    // Astrana:-only members (worklist row-click) skip the Pathways member
    // query entirely, so `loadedMember` is null and its name/dob/ipaCode are
    // never populated here — fall back to the worklist-carried extras
    // (astranaMemberName/astranaMemberDob) and `ctx.companyId` (already
    // resolved to astranaMemberCompanyId on this path — see its assignment
    // above) so the crosswalk has something to search on instead of
    // guaranteed-failing on undefined name. `MM/DD/YYYY` from the worklist
    // matches resolvePpMembId's own `toIsoDate` US-date branch, so no extra
    // normalization is needed here.
    return resolvePpMembId(ctx.apolloClient, {
      name: ctx.loadedMember?.name || ctx.astranaMemberName,
      dob: ctx.loadedMember?.dob || ctx.astranaMemberDob,
      ipaCode: ctx.loadedMember?.ipaCode || ctx.companyId,
      // Present for a Pathways-loaded member; absent on the worklist
      // (astrana:-only) path, which still relies on the name + DOB fallback.
      memberKeyId: ctx.loadedMember?.memberKeyId,
    });
  }).current;

  // ACT-1247 (native + iframe Create Auth) — `onAuthCreated` fires the moment
  // the auth submission SUCCEEDS, while the module's own post-submit
  // confirmation screen (AuthConfirmationPage for native / PP's in-frame
  // confirmation for iframe) is still mounted and waiting on the user to
  // click Done/close (see astrana-ui-modules Authorizations.tsx's AM-5412
  // comment: "onAuthCreated is now REFRESH-ONLY... does not close"). We used
  // to call `refreshNow()` here, which bumps `refreshKey` and — because
  // `refreshKey` feeds the `key` on the <GraphQLClientGate> wrapping the
  // WHOLE <MemberProfile> subtree below (see its usage further down) — forces
  // an immediate remount of that entire subtree. That destroyed the
  // confirmation screen's local React state before the user ever saw it and
  // reset the active tab back to Home. There is currently no host-facing
  // "the module actually closed" callback exposed by @astrana/ui's
  // Authorizations/CreateAuth props (only `onAuthCreated`; the real close
  // signal — CreateAuth's `onClose` — is wired internally to Authorizations.
  // tsx's own local `closeEmbed`, never forwarded to the host) — so we can't
  // defer the remount to an exact "closed" event without an upstream change.
  //
  // AM-5701: we previously also armed `markHandoffPending()` here, on the
  // theory that a subsequent tab switch would safely pick up the refresh.
  // That hook and its focus/visibilitychange arm have since been deleted
  // entirely (AM-5828 handoff purge) — dead code left over from an abandoned
  // "open PP in a new tab, detect the return via focus" handoff that nothing
  // called anymore. Best host-only fix here remains to do nothing: we don't
  // need any handoff-refresh path for the "new auth appears in the list"
  // case — leaving the Auths tab and returning re-mounts the module
  // (MemberProfile only renders the active tab), which refetches on its own.
  const stableOnAuthCreated = useRef(() => {}).current;

  // AM-5651 — "New Auth - Different Member" on the native Create-Auth
  // confirmation screen. astrana-ui-modules v0.4.36 (PR #356) threads
  // `onNewAuthDifferentMember` through Authorizations.types.ts's EmbedAuthProps
  // and Authorizations.tsx's <CreateAuth> render — before that release it only
  // existed on CreateAuth's own innermost props (host-inert). When the host
  // doesn't supply this callback, the confirmation screen falls back to
  // `onClose`, which just lands the user back on THIS member's profile. Route
  // to Home instead, whose member-search widget is where a brand-new lookup
  // for a different member starts (the old Search page is no longer a
  // destination — see handleBack below). Unmounting this route also drops
  // all local member-context state (ppMember, embedCtxRef, etc.) for free.
  const stableOnNewAuthDifferentMember = useRef(() => {
    navigate("/home");
  }).current;

  // Session-death handler for astrana-ui's internal Apollo client (care-team /
  // labs / eligibility / claims / assistant all query through it). Routes
  // through SessionGuard's modal so the user can sign back in inline
  // without leaving the current member-profile route. Ref-stable so
  // <GraphQLProvider> doesn't tear down its client on every render.
  const authFailureRef = useRef<() => void>(() => {});
  authFailureRef.current = () => triggerExpiryWarning("auth-failure");
  const onAuthFailure = useRef(() => authFailureRef.current()).current;

  if (!id) return <Navigate to="/members" replace />;

  // PP and connect-only (astrana:) members have no Pathways record — skip the
  // lookup entirely.
  const { data, loading, error } = useQuery<MemberByIdResult, MemberByIdVars>(
    MEMBER_BY_ID,
    { variables: { id }, skip: isPp || isAstranaOnly },
  );

  // Kept before any early return so hook order stays stable across renders.
  // Context fields are optional; the handler sends undefineds to the mutation
  // until the member query resolves. PP uses the carried PP fields directly.
  const loadedMember = data?.PathwaysMember ?? null;

  // Care Management market (Care at Home is CA/TX/RI only). Source: the
  // Pathways record's `address.state` (MEMBER_BY_ID). PP and connect-only
  // (astrana:) members have no Pathways record, so their state is unknown and
  // resolves to the fail-closed sentinel (CAH hidden) — see
  // careManagementMarket.ts for why undefined must never be passed.
  const careManagementMarket = toCareManagementMarket(loadedMember?.address?.state);

  // ACT-1342 — Quality Gaps checklist, now backed by providerPortalAPI REST
  // (the care-management-gql GraphQL path — connectQualityGapClosures /
  // setConnectQualityGapClosure — was ABANDONED; its table is being dropped).
  // No feature flag gates this (2026-07-28 directive: flags aren't how we hide
  // features here) — visibility is entirely the existing "HomeQualityGaps"
  // persona/featureCatalog gate, and write access is entirely the PP-session
  // requirement (see useQualityGapClosures's ensurePpSession/PpLoginModal
  // wiring below) — neither of those is a feature toggle.
  // Keyed on `astranaId`, not the Pathways Mongo `_id` the old GraphQL path
  // used as `memberObjectId` — the whole point of this redesign: it works
  // for a Pathways member (`loadedMember.astranaId`) AND a connect-only
  // fallback member (`astranaOnlyId`, no Pathways record at all), unlike the
  // old memberObjectId key which hard-required a Pathways record. Mirrors
  // `eobAstranaIdRaw`'s derivation below (same fallback chain) — a PP-search
  // (`isPp`) member still resolves to `undefined` here: it has neither a
  // Pathways record nor an astranaId source anywhere in this host today, so
  // the checklist stays disabled for that path rather than guessing an id
  // and risking a closure written against the wrong member (PHI).
  const qualityGapAstranaId =
    loadedMember?.astranaId ?? (isAstranaOnly ? astranaOnlyId : undefined);
  // Net gate for the INTERACTIVE checklist: a resolvable astranaId AND the
  // active persona hasn't opted this card out (see
  // `qualityGapsReadOnlyForPersona`'s doc above). False either strips the
  // whole checklist prop bundle at the injection site below (the GENUINE old
  // read-only card — `isChecklist` in astrana-ui-modules' QualityGaps.tsx is
  // computed as `!!onToggleGap` ALONE, so merely toggling `readOnly` would
  // still render a checklist, just a disabled one — the wrong outcome for a
  // persona opt-out) OR skips the closures fetch entirely (see the
  // `astranaId` arg below — never call providerPortalAPI for a persona that
  // can't interact with the result).
  const qualityGapChecklistActive = !!qualityGapAstranaId && !qualityGapsReadOnlyForPersona;
  // Same expression the card's own useQualityGaps hook defaults to
  // (astrana-ui-modules src/hooks/MemberProfile/useQualityGaps.ts) — using
  // anything else would silently mismatch the read the card itself performs.
  const qualityGapMeasureYear = new Date().getFullYear();
  const {
    closedGapIds: qualityGapClosedIds,
    // AM-6518 — the per-gap Mark-status pick, wired straight to the card's
    // controlled `gapResolutions`/`onSetResolution` pair. See
    // useQualityGapClosures.ts's docs for the dedupe with onToggleQualityGap
    // below (the card fires both for the same pick).
    gapResolutions: qualityGapResolutions,
    onSetResolution: onSetQualityGapResolution,
    onToggleGap: onToggleQualityGap,
    // `loading` IS consumed by @astrana/ui's shipped QualityGaps component
    // (v0.4.59, confirmed against astrana-ui-modules source):
    // `isLoading = loading || props.loading` — it's OR'd into the card's own
    // internal gaps-loading state, so it only ever ADDS a loading indicator,
    // never suppresses the card's own. `error` is NOT wired below: it's
    // present on the prop TYPE (inherited generically from every module's
    // `MemberProfileDataProp<T, U>`), but the shipped component only ever
    // renders its OWN internally-computed `useQualityGaps` error, never
    // `props.error` — passing it would type-check but render nothing, so
    // it's omitted rather than implying a UI effect that doesn't exist.
    loading: qualityGapLoading,
    // Confirmed against the shipped v0.4.59 component: `readOnly` IS a real,
    // rendered prop (`QualityGapsChecklistProps.readOnly` — "renders
    // checklist affordances but refuses input"). Set true only when the
    // initial closures GET failed (providerPortalAPI unreachable / endpoint
    // not yet deployed) — never for a successful empty result, which stays
    // fully interactive, and only ever meaningful while the checklist is
    // ACTIVE at all (see `qualityGapChecklistActive` above — a persona
    // opted OUT gets the genuine old card, not a disabled checklist). See
    // useQualityGapClosures.ts's `readOnly` doc.
    readOnly: qualityGapReadOnly,
  } = useQualityGapClosures({
    // Skip the closures GET/POST path entirely when the checklist isn't
    // active for this persona — reuses the hook's EXISTING "no astranaId"
    // skip condition rather than adding a parallel disabled/enabled flag.
    astranaId: qualityGapChecklistActive ? qualityGapAstranaId : undefined,
    measureYear: qualityGapMeasureYear,
    ppApiBaseUrl: import.meta.env.VITE_PROVIDER_PORTAL_API_BASE_URL,
    ppToken,
    ensurePpSession,
  });

  // P6d — `onMemberResolved` seam (see the prop's doc above). Same locally-
  // known name MemberSMS's `memberName` prop reads further down; astrana-only
  // members have neither source and never fire this (placeholder stays).
  const resolvedMemberName = isPp
    ? (ppMember?.fullName ?? undefined)
    : (loadedMember?.name ?? undefined);
  useEffect(() => {
    if (resolvedMemberName) onMemberResolved?.(resolvedMemberName);
  }, [resolvedMemberName, onMemberResolved]);

  const handleAssistantSend = useMemberAssistantSend(
    isPp
      ? {
          memberId: ppMemberId,
          membKeyId: ppMember?.membKeyId ?? undefined,
          companyId: ppCompanyId || undefined,
        }
      : {
          memberId: loadedMember?.memberPolicyId,
          membKeyId: loadedMember?.memberIdOthers?.apolloMemberKey,
          companyId:
            loadedMember?.ipaCode === "NA" ? undefined : loadedMember?.ipaCode,
        },
  );

  // AM-5412 — Duplicate auth deep-link (vertical slice). The PP tenant is the
  // member's IPA; "NA" means no tenant, so the handoff can't proceed. PP members
  // carry their tenant as ppCompanyId; Pathways members use the loaded ipaCode;
  // a connect-only (astrana:) member uses the host-carried extra if present
  // (worklist), else has none (genuine connect-only match / hard refresh).
  const companyId = isAstranaOnly
    ? astranaMemberCompanyId
    : isPp
      ? ppCompanyId || undefined
      : loadedMember?.ipaCode === "NA"
        ? undefined
        : loadedMember?.ipaCode ?? undefined;

  // Best-available PP member id hint; PP authoritatively resolves RmiMembId from
  // the authNo, so this only needs to be a hint.
  const handoffMemberIdHint = isAstranaOnly
    ? astranaMemberId || ""
    : isPp
      ? ppMemberId
      : loadedMember?.memberPolicyId ?? "";

  // WHO the visit-note drafter is about. Both of its identifiers — the human
  // member id it shows, and the internal numeric astranaId care-gql uses to
  // load the note's available lab results — come out of ONE call against ONE
  // member record, so they can never describe two different people. See
  // `visitNoteMemberIdentity.ts` for why that matters and what happens when the
  // id is not knowable (nothing is passed; the labs picker stays empty).
  const visitNoteMember = visitNoteMemberIdentity({
    routeId: id,
    loadedMember,
    astranaNavId: astranaOnlyId,
    astranaCarriedMemberId: astranaMemberId,
    ppMemberId,
  });

  // Create-Auth-V2 native migration, Phase 4 (ACT-1247) — DEV/local e2e only
  // (see featureFlags.nativeCreateAuth). Maps member-360's own member record
  // into the native module's `authMember` shape — see toAuthMember.ts for the
  // full field-by-field mapping + documented gaps. No connect-only (astrana:)
  // support: that path has no Pathways/PP member record to map from at all.
  const nativeAuthMember = isAstranaOnly
    ? undefined
    : isPp
      ? toAuthMemberFromPp(ppMember, ppMemberId, companyId)
      : loadedMember
        ? toAuthMemberFromPathways(loadedMember, companyId)
        : undefined;

  // "The PP user's id (same value used for PP calls)" — MUST be decoded from
  // whatever bearer `resolvedAccessToken` (below) actually sends, in EVERY
  // branch, not hardcoded to `ppToken` alone (AM-5713). PP-API's
  // `AuthMemberController.IsUserValid` compares the request body's
  // `UserIdModel.UserId` against the *bearer's* `sub` claim, so a mismatch
  // between the two is a 403 regardless of which side is "wrong".
  //
  // `identityToken` computes the SAME "real bearer or nothing" decision as
  // `resolvedAccessToken` below, but can't just READ `resolvedAccessToken`
  // (computed later) or `ppPreflight.hasLink` (the hook doesn't exist yet —
  // `ppPreflight`'s own `userId`/`userRole` args are THEMSELVES derived from
  // `identityToken`/`nativeUserId`, a chicken/egg) — so it takes the same
  // inputs directly, with `identityHasPpLink` standing in for
  // `ppPreflight.hasLink` (a same-render, pure-sync call to the same
  // `hasPersistedPpLink`, guaranteed to agree with it):
  //   - provider-portal-b2c primary: `ppToken` IS the primary session token
  //     → same PP `sub` as before, no regression.
  //   - Entra/Pathways + linked PP: `ppToken` is the linked PP bearer → PP
  //     `sub` (the original 403 fix).
  //   - linked (`identityHasPpLink`) or `ppEligible`, but no `ppToken` YET:
  //     `identityToken` (and `resolvedAccessToken`) withhold entirely
  //     (`undefined`) — no real bearer exists yet, so there is no real
  //     identity to decode and no real PP-API call is reachable in this
  //     window either (the module's write surface stays hidden until a real
  //     token lands). `identityHasPpLink` is checked HERE (unlike
  //     `resolvedAccessToken`, which doesn't need it — see that comment)
  //     because without `createAuthGateOpen`'s outer gate, a persona
  //     downgraded to no-"create" (`ppEligible: false`) while a PP link still
  //     persists would otherwise fall through to decoding the AMBIENT
  //     (non-PP) token as `UserId`, a guaranteed bearer/UserId mismatch that
  //     could spuriously trip the 401 `no-pp-permission` branch and
  //     invalidate/deny a perfectly good link for a reason unrelated to real
  //     PP eligibility.
  //   - not linked, not `ppEligible` (PP B2C not configured, or no resolvable
  //     identity email) and no `ppToken`: falls back to the ambient `token`
  //     → `identityToken` = that SAME `token` → `nativeUserId` decoded from
  //     it, exactly the pre-AM-5713 behavior (bearer and UserId stay
  //     self-consistent; this is the branch a ppToken-only fix regressed).
  // Cross-check against `resolvedAccessToken`'s own definition below if this
  // ever needs to change — they must never diverge. `resolveCreateAuthIdentityToken`
  // (auth/ppToken.ts) is the shared, unit-tested implementation of this chain.
  // ppUserIdFromClaims prefers `sub` (falling back to `oid`/`objectId`).
  const identityToken = resolveCreateAuthIdentityToken(ppToken, identityHasPpLink, ppEligible, token);

  // READ-scope bearer for @astrana/ui's `ppReadAccessToken` (Print). Same
  // resolution chain as `identityToken` above — including its deliberate
  // ambient-`token` fallback for the (!hasPpLink && !eligible) branch, so envs
  // with no PP B2C configured keep sending exactly the bearer they send today —
  // but keyed on `ppReadEligible` instead of `ppEligible`, i.e. NOT gated on
  // Auths:create. `undefined` here means "no bearer yet, but one may be
  // mintable": `stableEnsurePpReadSession` below covers that case.
  const ppReadAccessToken = resolveCreateAuthIdentityToken(
    ppToken,
    identityHasPpLink,
    ppReadEligible,
    token,
  );
  const nativeUserId = identityToken ? ppUserIdFromClaims(decodeJwtClaims(identityToken)) : undefined;

  // Same bearer as nativeUserId above. PP's real
  // `GetAuthSearchProviderList`/`AuthEnabledProviderList` 400 without
  // `userRole` alongside `userId` (see astrana-ui-modules-createauth-native's
  // CreateAuth.types.ts userRole JSDoc). PP's B2C custom attribute is
  // `extension_Role`; ppUserRoleFromClaims also checks generic `roles`/`role`
  // fallbacks. When the identity bearer carries no role claim, default to
  // "PROVIDER" (PP's baseline provider-facing role) so the provider-list
  // calls don't 400 outright.
  const nativeUserRole = identityToken
    ? (ppUserRoleFromClaims(decodeJwtClaims(identityToken)) ?? "PROVIDER")
    : undefined;

  // AM-5619 — Create Claim rides the PP identity, and FAILS CLOSED where
  // Create Auth falls back.
  //
  // Deliberately `ppToken`, NOT `identityToken`. `resolveCreateAuthIdentityToken`
  // ends with an ambient-token fallback for the (!hasPpLink && !ppEligible)
  // case, justified for Create Auth by "must keep behaving exactly as before
  // that feature landed" (see :1401). That justification does NOT transfer:
  // every Create Claim endpoint is now a real PP-API call behind APIM, which
  // authenticates on PP identity, so an ambient Entra bearer can never
  // legitimately serve one — it can only 401, while needlessly forwarding a
  // broader-audience token to a gateway it isn't scoped for. Note `ppEligible`
  // keys off `canCreateAuth` (Auths:create) while Create Claim gates on
  // Claims:submit (:838), so a Claims-only persona would otherwise land in
  // exactly that ambient branch and 401 anyway.
  //
  // `getPpToken` already fails closed on an expired or identity-mismatched
  // token, so this is re-checked for freshness every render.
  const claimBearer = ppToken;
  // Bearer and UserId MUST move together, with NO extra fallback. `nativeUserId`
  // (:1253) deliberately has no `accountLabel` fallback; adding one here would
  // (a) re-create the bearer/UserId divergence AM-5713 fixed — PP-API compares
  // body UserId to the bearer's `sub`, so a mismatch is a deterministic 403 —
  // and (b) put an EMAIL into `?userId=` query strings alongside `membKeyId`
  // (getCreateClaim, SearchCPTCodeByTypes), landing an identifier pair in APIM
  // / App Insights request logs on a PHI path. Same precedence as Create Auth.
  const claimUserId = claimBearer
    ? ppUserIdFromClaims(decodeJwtClaims(claimBearer))
    : undefined;

  // (The bearer ref + its layout effect are gone: `stableGetClaimAccessToken`
  // now resolves the token live from `ppSessionCtxRef` on every request, so
  // there is no snapshot to keep in sync — and nothing left to leak past
  // unmount/logout either.)

  // referringProvider: GAP — member-360 has no PCP/provider data in scope
  // here. MEMBER_BY_ID selects no provider fields, and Care Team is a
  // self-fetching library module the host never sees the data from. Omitted
  // (optional prop); useCreateAuthInit degrades gracefully with an
  // effectively-empty provider (its provider-scoped queries `skip`).

  // "Download EOB" gate. The claim-detail query the library runs is scoped by
  // numeric astranaId (same as the Claims module); with none, the query would
  // return nothing, so the CTA is withheld rather than rendered permanently
  // disabled. The header demographics the claim payload lacks (name / DOB /
  // health plan) are no longer assembled here — MemberProfile injects them into
  // the Claims module from the patient profile it has already fetched.
  const eobAstranaIdRaw = loadedMember?.astranaId ?? (isAstranaOnly ? astranaOnlyId : undefined);
  const eobAstranaId =
    eobAstranaIdRaw != null && eobAstranaIdRaw !== "" && Number.isFinite(Number(eobAstranaIdRaw))
      ? Number(eobAstranaIdRaw)
      : undefined;

  // ACT-1247 — populate embedCtxRef with live values so the stable
  // stableResolvePpMemberId wrapper (captured before the early-return guards)
  // always closes over the current render's member data.
  embedCtxRef.current = {
    companyId,
    memberIdHint: handoffMemberIdHint,
    isPp,
    ppCompanyId,
    ppMemberId,
    loadedMember: loadedMember
      ? {
          name: loadedMember.name,
          dob: loadedMember.dob,
          ipaCode: loadedMember.ipaCode,
          // Lets the Create-Auth crosswalk resolve RmiMembId by exact point
          // lookup instead of guessing from name + DOB (see resolvePpMembId).
          memberKeyId: loadedMember.memberIdOthers?.apolloMemberKey,
        }
      : null,
    apolloClient,
    astranaMemberName,
    astranaMemberDob,
  };

  // ACT-1247 SSO-PP auth-linking Phase 1 (design doc §5a) — silent
  // eligibility pre-flight. Resolves BOTH user- and patient-EZCAP
  // eligibility off a persisted PP link before Create Auth is surfaced, so
  // an ineligible click fails up-front and explained instead of late (an
  // APIM 401 / empty GetAuthMember at mint time). Positioned here (after
  // embedCtxRef.current) — same hook-order rationale as
  // visibleModulesWithExtras below — so companyId/handoffMemberIdHint are in
  // scope. This does NOT change persona policy: `canCreateAuth` (the persona
  // gate, above) is untouched; this only adds a second, narrower AND-gate.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const ppPreflight = usePpCreateAuthPreflight({
    identityEmail,
    ppApiBaseUrl: import.meta.env.VITE_PROVIDER_PORTAL_API_BASE_URL,
    companyId,
    memberId: handoffMemberIdHint || undefined,
    userId: nativeUserId,
    userRole: nativeUserRole,
  });

  // Net gate (design doc §5a): personaAllows AND (preflightPassed OR
  // offerLink). `ppPreflight.gateOpen` reads true for BOTH the "eligible"
  // and the "unlinked / offer-link" cases — an unlinked account is never
  // hard-hidden, clicking still opens the existing seamless PpLoginModal via
  // ensurePpSession — and false only once a LINKED account has been
  // POSITIVELY found ineligible. When false, the write props below are
  // stripped exactly like the persona gate already does, hiding Create /
  // Duplicate / Modify with no @astrana/ui change.
  const createAuthGateOpen = canCreateAuth && ppPreflight.gateOpen;

  // AM-5768 — the ACTING USER's IPA, which scopes @astrana/ui Create Auth's
  // Referring Provider panel. A DIFFERENT axis from `companyId` (the viewed
  // member's tenant / X-Backend-Id): PP's GetAuthSearchProviderList resolves
  // that panel from the user's own PortalManager grants, so passing the
  // member's IPA offered the wrong slice of the user's referral network. See
  // useActingUserIpa.ts for the full contract and resolution order.
  //
  // Gated on `createAuthGateOpen` so the EntitledIpas POST only fires where
  // Create Auth is actually reachable — the same posture AdtBell.tsx protects
  // by lazy-importing the worklist gate. Resolves to `undefined` on any failure
  // or an unresolvable multi-IPA case, which makes @astrana/ui fall back to
  // `companyId` (prior behavior) rather than break Create Auth.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const actingUserIpa = useActingUserIpa({
    enabled: createAuthGateOpen,
    memberCompanyId: companyId,
  });

  // Surface the pre-flight reason once per verdict (toast — @astrana/ui
  // exposes no prop to show inline copy on the Auths tab). Fires only for a
  // definitively ineligible LINKED account; the "unlinked" case's messaging
  // is the existing PpLoginModal itself, not a separate toast.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (ppPreflight.hasLink && !ppPreflight.gateOpen && ppPreflight.reason) {
      toast.error(PP_ELIGIBILITY_REASON_TEXT[ppPreflight.reason]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ppPreflight.hasLink, ppPreflight.gateOpen, ppPreflight.reason]);

  // Create-Auth accessToken decision (ACT-1247 follow-up; sentinel removed
  // 2026-07-24 — see the removed-sentinel note near the top of this file):
  //   - already have a valid PP token (own PP session or a stashed one from
  //     an earlier PpLoginModal run) -> pass it through, real token.
  //   - no PP token, but `ppEligible` (persona allows create, PP B2C is
  //     configured, identity resolvable) -> withhold entirely (`undefined`).
  //     This covers BOTH a linked identity whose silent refresh hasn't
  //     landed yet (`usePpCreateAuthPreflight`'s `ensurePpAccessTokenDetailed`/
  //     `ropcRefresh`, ppToken.ts, is a genuine network round trip — there is
  //     a real window where `getPpToken()`, synchronous/no-I/O, still reads
  //     the OLD expired stash) AND a never-linked identity: either way the
  //     module must NOT mount without a real bearer (a placeholder string
  //     would make `canSelfHostEmbed()` truthy and the module would fire its
  //     own eager GetAuthMember/ExpiryDays/MediCalCode/RuleCodes/... lookup
  //     queries immediately — those are NOT gated by `resolvePpMemberId`/
  //     `ensurePpSession`, which only covers the Create-button's member-id
  //     resolution + mint step, and are never retried once a real token
  //     lands a render later — confirmed live: every such request 401's with
  //     the literal placeholder string as `Authorization`). No data-read
  //     regression: the Auths tab's own (non-PP) auths list is unaffected —
  //     only the PP write surface stays hidden until a real token exists.
  //     A tokenless `ppEligible` identity's entry point back to
  //     `ensurePpSession`/PpLoginModal is now a host-injected `actions` entry
  //     on the Auths ModuleObject itself (`showPpLinkAction` below) — an
  //     IN-PLACE label swap in the SAME header slot the native "Create"
  //     button occupies, NOT a separate floating affordance and NOT the
  //     mislabeled `onCreateAuth` fallback (whose button text is the
  //     module's own hardcoded "Create" copy, not ours) — see that const's
  //     own comment for the full mechanism and why this is required (without
  //     it, a never-linked identity would have no way to ever trigger PP
  //     login once the module can no longer render a clickable surface on
  //     its own).
  //   - otherwise -> fall back to the ambient session `token`, exactly the
  //     pre-existing expression (`canCreateAuth ? token : undefined`) below.
  //     Deliberately NOT `undefined` here: environments where PP B2C isn't
  //     configured, or an identity with no resolvable email, must keep
  //     behaving exactly as before this feature landed.
  //   - `createAuthGateOpen` (ACT-1247 SSO-PP auth-linking Phase 1) folds in
  //     the eligibility pre-flight on top of the persona gate — see above.
  const resolvedAccessToken: string | undefined = !createAuthGateOpen
    ? undefined
    : (ppToken ?? (ppEligible ? undefined : (token ?? undefined)));

  // "Link Provider Portal account" IN-PLACE LABEL SWAP (pilot for a reusable
  // PP-API-gated-action pattern — Claims/PPI/etc. will follow the same
  // shape). Replaces the removed sentinel's role as the ONLY entry point
  // into `ensurePpSession`/PpLoginModal for a tokenless identity — see
  // resolvedAccessToken's comment above. True whenever this identity is
  // eligible-by-persona-and-config but has no working bearer AND no
  // persisted link currently worth waiting on: `!ppPreflight.hasLink` covers
  // both a NEVER-linked identity (hasLink is false immediately, no silent
  // path running at all) and a linked identity whose silent refresh has been
  // HUNG long enough to trip `usePpCreateAuthPreflight`'s
  // `SILENT_MINT_TIMEOUT_MS` fail-open (without that timeout, a genuinely
  // hung refresh would leave `hasLink` true forever and this action would
  // never appear either — a second, subtler deadlock).
  const ppLinkUnresolved = ppEligible && !ppToken && !ppPreflight.hasLink;

  // Debounce: `ppLinkUnresolved` can be transiently true during NORMAL login
  // hydration for someone who does NOT need to link at all — e.g. an
  // already-linked SSO identity in the brief window before `ppToken`/
  // `ppPreflight.hasLink` resolve, or (worse) a provider-portal-b2c PRIMARY
  // user if `primarySessionToken` hydrates a render after `identityEmail`/
  // `ppEligible` (their `ppToken` IS `primarySessionToken` via `getPpToken`,
  // so it's momentarily `undefined` too). Harmless either way (clicking
  // would just no-op through `ensurePpSession`'s already-has-a-token branch),
  // but relabeling the Auths header's action away from "Create" and back at
  // someone already signed in as PP is a confusing flicker.
  // `PP_LINK_ACTION_DEBOUNCE_MS` requires the unresolved condition to hold
  // CONTINUOUSLY before swapping the label over — long enough to swallow the
  // sub-second hydration flicker, short enough to still promptly offer a
  // genuinely-unlinked user the link action. Same arm-while-true/disarm-the-
  // moment-it-flips-false shape as `usePpCreateAuthPreflight`'s
  // `SILENT_MINT_TIMEOUT_MS` block.
  //
  // `showPpLinkAction` gates a host-injected top-level `actions` entry on
  // the Auths ModuleObject below — `Module<T>.actions?: ModuleAction[]` is a
  // GENERIC field on EVERY module type (MemberProfile.types.d.ts), the same
  // mechanism already used for the Claims tab's "Create Claim" button, and
  // it composes CLEANLY with the module's own native create control
  // (confirmed by reading the shipped Authorizations bundle):
  //   `const createHandler = selfHost ? handleSelfHostedCreate : onCreateAuth;`
  //   `const createAction = createHandler ? { label: "Create", ... } : null;`
  //   `const sectionActions = createAction ? [...actions, createAction] : actions;`
  // We never pass `onCreateAuth` at all (see EmbedAuthProps below) — so
  // whenever `selfHost` is false (no real accessToken), `createHandler`/
  // `createAction` are both null/falsy and `sectionActions` is JUST our
  // injected `actions` array: exactly ONE button in that header slot, with
  // OUR label ("Link Provider Portal account") and OUR onClick
  // (`handleLinkPpAccount`) — never the module's own "Create" text wired to
  // a login flow, which is the mislabeled outcome this pilot is explicitly
  // avoiding. Once a real `ppToken` lands, we stop injecting `actions`
  // entirely and `selfHost` is true, so the module's OWN native "Create"
  // button renders alone instead — a genuine in-place swap, not two buttons
  // stacked.
  const [showPpLinkAction, setShowPpLinkAction] = useState(false);
  // Per-view dismissal of the FLOATING "Link Provider Portal account" nudge
  // (bottom-right). Clicking its × hides the floating card for the current
  // view; it re-appears if the eligible-but-unlinked condition clears and then
  // arises again (reset alongside showPpLinkAction below). Does not affect the
  // in-place label swap in the Auths header — only the floating affordance.
  const [ppLinkDismissed, setPpLinkDismissed] = useState(false);
  useEffect(() => {
    if (!ppLinkUnresolved) {
      setShowPpLinkAction(false);
      setPpLinkDismissed(false);
      return;
    }
    const timer = setTimeout(() => setShowPpLinkAction(true), PP_LINK_ACTION_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [ppLinkUnresolved]);

  // Stable onClick for the injected "Link Provider Portal account" action —
  // identical action to what the module's own Create button would do once a
  // real token exists: get a real PP bearer, trying a silent mint first.
  const handleLinkPpAccount = useRef(() => {
    void ensurePpSession().catch(() => {});
  }).current;

  // ── Native Create Claim: derived member context ─────────────────────────
  // Computed at this point (not inside the memo callback below) so they land
  // cleanly in the memo's dependency array — mirrors companyId/
  // handoffMemberIdHint's positioning above.
  const claimMemberId = toClaimMemberId(isPp, ppMemberId, loadedMember);
  // Built from the gate's resolved seed and NOTHING else. There is deliberately
  // no per-path mapper here any more: the previous pair scavenged
  // `membKeyId`/`hpCode`/`startDate` off whichever member object the profile
  // happened to hold, which for a Pathways member meant `RmiStartDate:
  // undefined` (no such field exists on PathwaysMember) and a Mongo-sourced
  // health plan code — three values that can never form EZCap's composite key.
  // See members/toClaimContext.ts.
  const claimMemberCtx = claimSeed ? toClaimMemberFromSeed(claimSeed) : undefined;
  // What the gate resolves the member FROM, when it has to (a PP-path profile
  // normally hands it a complete row instead — see `ppRecord` at the call site).
  // Mirrors stableResolvePpMemberId's construction, including the worklist
  // (astrana:-only) name/DOB fallback, so both write flows resolve the same way.
  const claimResolveMember = {
    name: loadedMember?.name || astranaMemberName,
    dob: loadedMember?.dob || astranaMemberDob,
    ipaCode: loadedMember?.ipaCode || companyId,
    memberKeyId: loadedMember?.memberIdOthers?.apolloMemberKey ?? ppMkey,
    // Lets the resolver skip straight to an exact-id PP lookup. Populated on the
    // PP path (search row, or recovered by the mkey point lookup on a hard
    // refresh); empty for a Pathways member, whose id is a Mongo policy
    // surrogate and must never be sent as an RmiMembId.
    membId: isPp ? ppMemberId : undefined,
  };
  // Identity of the DECISION the gate made: the (member, health plan, tenant)
  // triple, not just the member. The health plan comes from the resolved seed —
  // the same row the claim is filed against — so this compares the two live
  // values (member id, tenant) against the ones the provider was cleared for.
  // See claimGateKey.
  const claimGateKeyNow = claimGateKey(claimMemberId, claimSeed?.hpCode, companyId);
  // The gate-resolved provider, but ONLY while the gate's decision still applies
  // (see claimProviderFor). Derived rather than reset in an effect so a stale
  // pairing is never observable for even one render — if the member, their health
  // plan or their tenant changes underneath, this is `null` immediately and the
  // form's open condition below fails closed, sending the user back through the
  // gate.
  const claimProviderForMember = claimProviderStillValidForMember(
    claimProvider,
    claimProviderFor,
    claimGateKeyNow,
  );

  // Extend the full (NOT yet persona-filtered) tab list with:
  //   1. PP embed props injected into the Auths group so Create / Duplicate /
  //      Modify render in-place (ACT-1247).
  //   2. The flag-gated tabs — Notes, Scribe, Documents.
  //
  // AC-ADM-3: this memo used to map over the already-filtered `visibleModules`
  // and then append Documents/Scribe as post-filter "extras", which is exactly
  // how those two tabs escaped the persona gate ("Documents tab — always
  // visible, no persona gate"). It now maps over the UNFILTERED `allModules`,
  // and the single `useVisibleModules` pass runs on the result — so an appended
  // tab is gated identically to a declared one. The feature flags still decide
  // whether a group is CONSTRUCTED at all; the gate decides who SEES it.
  //
  // POSITION NOTE: This memo intentionally sits AFTER the companyId /
  // handoffMemberIdHint derivations and embedCtxRef.current assignment so those
  // values are in scope as direct deps and passed directly into the Auths props —
  // no stale ref reads. Both companyId and handoffMemberIdHint are in the dep
  // array so the memo recomputes when the MEMBER_BY_ID query resolves (Pathways)
  // or when PP state changes.
  //
  // HOOK-ORDER NOTE: This useMemo sits after the `if (!id) return` guard (L308)
  // and after useQuery / useMemberAssistantSend — identically to those hooks.
  // This is a pre-existing hooks-after-early-return pattern in this file;
  // relocating the memo here does not worsen it (hook count is unchanged on every
  // render path where !id is true or false).
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const allModulesWithExtras = useMemo((): ModuleGroup[] => [
    // Inject embed props into the Auths group so the Authorizations module
    // self-hosts Create / Duplicate / Modify in-place (ACT-1247). All other
    // groups pass through unchanged.
    ...allModules.map((group) => {
      // ACT-1342 — inject the Quality Gaps checklist props onto the Home
      // card's QUALITY_GAPS entry AT RENDER TIME (mapping over `allModules`,
      // whose Home group already carries the persona-filtered CARD list from
      // useVisibleHomeCards — the whole-TAB gate now runs after this memo,
      // which does not affect this injection — never the module-level
      // `HOME_CARDS_DEFAULT` constant — that array is read identity-stably
      // across renders precisely because it never changes). The WHOLE prop
      // bundle (closedGapIds/onToggleGap/loading/readOnly) is attached ONLY
      // when `qualityGapChecklistActive` is true (a confirmed astranaId AND
      // the persona hasn't opted out via "qualityGapsReadOnly" — see that
      // const's derivation above). When false, NONE of these props are
      // passed — deliberately NOT just `onToggleGap` — because
      // astrana-ui-modules' QualityGaps.tsx derives its whole mode as
      // `const isChecklist = !!onToggleGap` alone; omitting only that one
      // prop while still passing e.g. `readOnly` would still leave
      // `isChecklist` false (harmless) but passing `closedGapIds` alone with
      // no handler is meaningless clutter. Omitting the entire bundle is
      // what makes the card render EXACTLY as it always did (the genuine old
      // read-only card), not a disabled/frozen checklist.
      if (group.tabName === "Home" && group.modules) {
        return {
          ...group,
          modules: group.modules.map((m) =>
            typeof m === "object" &&
            m !== null &&
            "type" in m &&
            m.type === ModuleType.CASES
              ? {
                  ...m,
                  props: {
                    ...m.props,
                    careManagement: {
                      ...(m.props as { careManagement?: object } | undefined)
                        ?.careManagement,
                      market: careManagementMarket,
                    },
                  },
                }
              : typeof m === "object" &&
                  m !== null &&
                  "type" in m &&
                  m.type === ModuleType.QUALITY_GAPS &&
                  qualityGapChecklistActive
              ? {
                  ...m,
                  props: {
                    ...m.props,
                    closedGapIds: qualityGapClosedIds,
                    // AM-6518 — see the destructure above for the dedupe
                    // contract with onToggleGap.
                    gapResolutions: qualityGapResolutions,
                    onSetResolution: onSetQualityGapResolution,
                    onToggleGap: onToggleQualityGap,
                    // OR'd into the card's own internal gaps-loading state
                    // (see the destructure above) — never suppresses it.
                    loading: qualityGapLoading,
                    // True only when the closures GET failed — never for a
                    // successful empty result (see the destructure above).
                    readOnly: qualityGapReadOnly,
                  },
                }
              : m,
          ),
        };
      }
      // ACT-1253 — inject the Outreach WRITE gate. `canCreate` is the persona
      // `create` action; withheld/false → the module renders no create surface.
      if (group.tabName === "Outreach" && group.modules) {
        return {
          ...group,
          modules: group.modules.map((m) =>
            typeof m === "object" &&
            m !== null &&
            "type" in m &&
            m.type === ModuleType.OUTREACHES
              ? {
                  ...m,
                  props: {
                    ...m.props,
                    canCreate: canCreateOutreach,
                    // ACT-1253 Phase-1b — pass the Amazon Connect config so the
                    // module can offer click-to-call. Withheld (undefined) ⇒
                    // the module stays log-only. `canPlaceCall` is derived
                    // inside the module (config present + real Pathways record).
                    amazonConnectInstanceUrl: AMAZON_CONNECT_INSTANCE_URL,
                    amazonConnectLoginPopup: AMAZON_CONNECT_LOGIN_POPUP,
                  },
                }
              : m,
          ),
        };
      }
      return group.tabName !== "Auths" || !group.modules
        ? group
        : {
            ...group,
            modules: group.modules.map((m) =>
              typeof m === "object" &&
              m !== null &&
              "type" in m &&
              m.type === ModuleType.AUTHORIZATIONS
                ? {
                    ...m,
                    // "Link Provider Portal account" in-place label swap — a
                    // top-level `actions` entry (same generic
                    // `Module<T>.actions?: ModuleAction[]` field the Claims
                    // tab's "Create Claim" button already uses below), NOT
                    // nested under `props`. Composes cleanly with the
                    // module's own native "Create" control: it appends its
                    // OWN `createAction` to whatever `actions` array we pass
                    // ONLY when `onCreateAuth`/`selfHost` produced one — we
                    // never pass `onCreateAuth` (see EmbedAuthProps below),
                    // so when there's no real token this injected entry is
                    // the ONLY action, correctly labeled; once a real token
                    // lands we stop injecting it and the module's own native
                    // "Create" renders alone instead. See `showPpLinkAction`'s
                    // doc comment above for the full mechanism + bundle-read
                    // verification.
                    actions: showPpLinkAction
                      ? [
                          ...((m as ModuleObject).actions ?? []),
                          {
                            label: "Link Provider Portal account",
                            variant: "primary" as const,
                            beforeIcon: "plus" as const,
                            onClick: handleLinkPpAccount,
                          },
                        ]
                      : (m as ModuleObject).actions,
                    props: {
                      ...m.props,
                      // PP base URLs — static env access (deliberate; see
                      // docs/architecture-overview.md on Vite static replacement).
                      // Gated on `createAuthGateOpen` (persona AND the ACT-1247
                      // SSO-PP eligibility pre-flight, see above): when either the
                      // persona lacks "create" OR a linked account has been
                      // positively found ineligible, strip all write props so the
                      // lib's canSelfHostEmbed() returns false and hides Create /
                      // Duplicate / Modify controls.
                      ppWebBaseUrl: createAuthGateOpen
                        ? import.meta.env.VITE_PROVIDER_PORTAL_WEB_BASE_URL
                        : undefined,
                      // Native module endpoints are relative (AuthMember/POS)
                      // and need the .NET-8 `/api` base. Create Auth is now
                      // ALWAYS native (the iframe is removed, useNativeCreateAuth
                      // is unconditionally true), so append `/api` unconditionally.
                      // Previously this was gated on featureFlags.nativeCreateAuth,
                      // so a deployed env with the flag off served the bare base
                      // and every native call 404'd (…/private/AuthMember/… with
                      // no /api). Decoupled from the flag to match the native path.
                      ppApiBaseUrl: createAuthGateOpen
                        ? `${import.meta.env.VITE_PROVIDER_PORTAL_API_BASE_URL}/api`
                        : undefined,
                      // Real PP bearer or `undefined` — never a placeholder
                      // (see resolvedAccessToken's own doc comment above).
                      accessToken: resolvedAccessToken,
                      // READ-ONLY PP scope — deliberately UNGATED by
                      // `createAuthGateOpen`. The three props above are the
                      // WRITE scope: stripping them hides Create / Duplicate /
                      // Modify (canSelfHostEmbed() goes false), and it used to
                      // hide the read-only Print + Symptoms actions with them.
                      // These `ppRead*` props feed Print alone, so an SSO user
                      // whose persona lacks Auths:create — or whose linked PP
                      // account the create pre-flight rejected — can still print
                      // an auth. Never widen these into the write props.
                      ppReadApiBaseUrl: `${import.meta.env.VITE_PROVIDER_PORTAL_API_BASE_URL}/api`,
                      ppReadAccessToken,
                      // Supplied ONLY while no bearer exists yet: its presence
                      // is what tells the module "this identity can obtain a PP
                      // bearer", so Print renders tokenless and links on click.
                      // Withheld once a token exists (nothing to mint) and for
                      // an identity that can never hold one (no PP B2C config /
                      // no resolvable identity email) — Print then stays hidden.
                      ensurePpReadSession:
                        !ppReadAccessToken && ppReadEligible
                          ? stableEnsurePpReadSession
                          : undefined,
                      // companyId and memberIdHint are derived from live query
                      // data (companyId from ipaCode, handoffMemberIdHint from
                      // memberPolicyId) and are in scope here because the memo is
                      // positioned after both derivations. They are also in the dep
                      // array so the memo recomputes when the member query resolves.
                      companyId: companyId ?? undefined,
                      // AM-5768 — acting user's IPA, scoping the Referring
                      // Provider panel ONLY. Leaves `companyId` (above) as the
                      // member tenant for routing/hydration. `undefined` here
                      // makes @astrana/ui fall back to `companyId`.
                      providerScopeCompanyId: actingUserIpa,
                      memberIdHint: handoffMemberIdHint || undefined,
                      resolvePpMemberId: createAuthGateOpen
                        ? stableResolvePpMemberId
                        : undefined,
                      onAuthCreated: stableOnAuthCreated,
                      // AM-5651 — see stableOnNewAuthDifferentMember's
                      // definition above for the "why /members with no state"
                      // call. Real as of @astrana/ui v0.4.36 (PR #356) — verify
                      // via typecheck that this is a genuinely typed prop on
                      // EmbedAuthProps, not a loose-typing no-op.
                      onNewAuthDifferentMember: stableOnNewAuthDifferentMember,
                      onError: (msg: string) => {
                        // A deliberate PpLoginModal cancel aborts cleanly —
                        // no mint happened, no panel opened, so no toast.
                        // `.includes` (not `===`): the module forwards
                        // `err.message` verbatim today, but this must not
                        // regress into a leaked-marker toast if a future
                        // version wraps/prefixes the message.
                        if (msg.includes(PP_LOGIN_CANCELLED_MESSAGE)) return;

                        // Modify (astrana-ui-modules Authorizations.tsx
                        // `resolvedAuthActions`) has no native impl and always
                        // renders the iframe embed, which mints straight off
                        // the live accessToken ref with no pre-mint seam to
                        // await ensurePpSession on. Now that accessToken is
                        // never a placeholder (see the removed-sentinel note
                        // near the top of this file), Modify can only even
                        // MOUNT with a real token in the first place — so a
                        // mint failure reaching this handler means that real
                        // token went stale/invalid in the split second
                        // between render and mint (an expiry race), not a
                        // fabricated bearer. Duplicate no longer hits this
                        // branch at all: as of @astrana/ui v0.4.41 (PR #370)
                        // it awaits resolvePpMemberId()/ensurePpSession before
                        // mounting, same as Create. Kept for Modify as a
                        // defensive fallback: when a mint fails AND we
                        // currently hold no real, identity-verified PP token,
                        // prompt for credentials instead of a raw error
                        // toast, so Modify gets the same seamless recovery as
                        // Create/Duplicate (the user just needs to retry the
                        // click afterwards; this does not auto-retry mint).
                        const noRealPpToken =
                          !getPpToken(selectedProvider, primarySessionToken, identityEmail);
                        if (MINT_FAILURE_PATTERN.test(msg) && noRealPpToken) {
                          void ensurePpSession().catch(() => {});
                          return;
                        }

                        toast.error(msg);
                      },
                      returnUrl: window.location.href,
                      // Theme propagation: initial value travels in PP_EMBED_HANDOFF;
                      // live changes are re-sent as PP_EMBED_THEME by the hook.
                      theme: isDark ? "dark" : "light",
                      // Create-Auth-V2 native migration, Phase 4 (ACT-1247) —
                      // DEV/local e2e only. Flag default-off so the iframe
                      // embed above stays the shipped path everywhere else.
                      // See toAuthMember.ts for the authMember mapping +
                      // documented gaps.
                      //
                      // ALWAYS native — the PP iframe embed is removed (it
                      // didn't work cross-origin), so Create/Duplicate use the
                      // native @astrana/ui module unconditionally, no longer
                      // gated on the VITE_FEATURE_NATIVE_CREATE_AUTH flag.
                      // WHO can create is still governed separately by
                      // `canCreateAuth` (persona "Auths"/create action, OR the
                      // Provider-Portal-login demo bypass): when false, the
                      // connection props above are nulled so the lib's
                      // canSelfHostEmbed() is false and the whole create
                      // surface is hidden. (Modify has no native impl yet, so
                      // it would still fall back to the lib's iframe — out of
                      // scope for the current native Create/Duplicate flow.)
                      useNativeCreateAuth: true,
                      authMember: nativeAuthMember,
                      referringProvider: undefined,
                      userId: nativeUserId,
                      userRole: nativeUserRole,
                      userEmail: accountLabel ?? undefined,
                      // AM-5694 (host side) — track the native form's dirty
                      // state so in-app navigation (handleBack, EHR-switch
                      // auto-nav, SwitchPatientModal) and the browser
                      // Back/Forward guard (useUnsavedChangesGuard) can gate
                      // on it. Only fires when useNativeCreateAuth is set
                      // (true here, see above).
                      onDirtyChange: stableOnAuthDirtyChange,
                    },
                  }
                : m,
            ),
          };
    }).map((group): ModuleGroup => {
      // Native Create Claim (feat/create-claim-native-host) + "Print EOB"
      // (feat/print-eob) both target the Claims tab — combined here so
      // neither rewrite clobbers the other's.
      if (group.tabName !== "Claims" || !group.modules) {
        return group;
      }

      // OPEN: render ONLY the native CreateClaim form — the Medical Claims
      // list (and therefore Print EOB, which only makes sense against the
      // list/detail view) is intentionally NOT rendered underneath, mirroring
      // how the Auths tab swaps list → embed. The form's Cancel/Close fires
      // onClose → setCreateClaimOpen(false), returning to the list.
      // `claimProvider` and `claimMemberCtx` are part of the OPEN condition, not
      // just props: the form must never mount without a real claim-write provider
      // AND a resolved PP member row, both from the entry gate (see
      // CreateClaimGate). Belt-and-braces — the only setter of `createClaimOpen`
      // is the gate's onProceed, which always supplies both.
      if (canCreateClaim && createClaimOpen && claimProviderForMember && claimMemberCtx) {
        return {
          ...group,
          modules: [
            <CreateClaim
              // CONSTANT key — deliberately NOT keyed on bearer presence.
              //
              // An earlier revision used
              // `key={claimBearer ? "native-create-claim" : "...-pending"}` to
              // rescue the mount race (useCreateClaimInit fires GetPos and
              // GetInvalidPrimDxInitials gated only on `companyId`, never
              // refetching once the token lands). That cure is far worse than
              // the disease: `ppToken` is recomputed WITH A FRESHNESS CHECK on
              // every render, so the moment the B2C token expires mid-form the
              // key flips to "pending" and React UNMOUNTS <CreateClaim> — wiping
              // every field of a part-filled CMS-1500, then remounting again
              // when the refresh lands. It would trade a 401-on-submit for
              // silent data loss, in exactly the long-lived-form scenario the
              // re-mint work exists to support.
              //
              // The mount race is now closed at the source instead: the
              // "Create Claim" onClick awaits ensurePpSession(), so the form
              // cannot mount without a bearer, and getAccessToken silently
              // refreshes per-request thereafter.
              key="native-create-claim"
              action={createClaimAction}
              memberId={claimMemberId}
              member={claimMemberCtx}
              // The provider the entry gate resolved: a real claim-write grant
              // from the acting IPA. `RmiProvKeyId` is what the module forwards
              // as the `provKeyId` param of `GET ClaimMember/CreateClaim` (a
              // GUID PP-API parses with an unguarded `new Guid(...)` and uses
              // for the claim's billing provider / facility / provider NPI), and
              // `RmiClass` drives the module's specialist-requires-referring
              // validation (`renderingProviderRmiClass`).
              //
              // NOTE: `CreateClaimProviderContext` (@astrana/ui v0.4.71) exposes
              // ONLY these two fields — there is no `RmiCompanyId` on it, and
              // the module reads none (verified against the shipped bundle: the
              // only `providerSeed.*` reads are RmiProvKeyId and RmiClass). The
              // provider's IPA reaches PP-API via the module's own `companyId`
              // prop / X-Backend-Id instead, and the gate has already
              // constrained the selection to that same IPA.
              provider={{
                RmiProvKeyId: claimProviderForMember.RmiProvKeyId,
                RmiClass: claimProviderForMember.RmiClass ?? undefined,
              }}
              // dateOfService: TODO(create-claim P2) — real DOS context TBD
              dateOfService={undefined}
              // Config — static env access (deliberate Vite static replacement;
              // see docs/architecture-overview.md). Native path needs /api suffix.
              // BLOCKED ON SAM (AM-5619): the PP-API claims endpoints are not
              // yet deployed. ppApiBaseUrl is wired correctly for when they land.
              ppApiBaseUrl={`${import.meta.env.VITE_PROVIDER_PORTAL_API_BASE_URL}/api`}
              companyId={companyId ?? ""}
              getAccessToken={stableGetClaimAccessToken}
              // User context — userId from sub/oid claim, role from currentUser.
              userId={claimUserId ?? ""}
              userRole={claimUserRole}
              userEmail={accountLabel ?? undefined}
              // PHI/AUDIT guard: isReadOnly forwards peek/impersonation state.
              // TODO(create-claim P6 SHIP-GATE): replace false with real peek
              // state once member-360-standalone exposes one for Claims.
              isReadOnly={isClaimReadOnly}
              readOnlyReason={claimReadOnlyReason}
              // Callbacks
              onClaimCreated={stableOnClaimCreated}
              onClose={stableOnClaimClose}
              onError={stableOnClaimError}
              onDirtyChange={stableOnClaimDirtyChange}
              onAnalyticsEvent={stableOnClaimAnalytics}
              theme={isDark ? "dark" : "light"}
            />,
          ],
        };
      }

      // CLOSED (list view): inject the "Create Claim" header action (if the
      // persona has the Claims "submit" action) AND the per-claim "Print EOB"
      // claimActions render-prop (if the persona has "printEob" AND we have a
      // numeric astranaId to scope the claim-detail query) onto the SAME
      // Claims ModuleObject — both coexist: `actions` is a top-level Module
      // field, `claimActions` lives under `props`, so spreading both is safe.
      return {
        ...group,
        modules: group.modules.map((m) => {
          if (
            typeof m !== "object" ||
            m === null ||
            !("type" in m) ||
            (m as ModuleObject).type !== ModuleType.CLAIMS
          ) {
            return m;
          }
          const claimsModule = m as ModuleObject;
          return {
            ...claimsModule,
            ...(canCreateClaim
              ? {
                  actions: [
                    ...(claimsModule.actions ?? []),
                    {
                      label: "Create Claim",
                      variant: "primary" as const,
                      beforeIcon: "plus" as const,
                      // AM-5619 — PP-login gate, the Claims analogue of
                      // `createAuthGateOpen`. Every Create Claim endpoint is a
                      // real PP-API call behind APIM, so an identity with no PP
                      // bearer can only 401. Rather than open a form that is
                      // guaranteed to fail, ensure a PP session FIRST.
                      //
                      // ensurePpSession is a no-op when a live token already
                      // exists (incl. PP-primary sessions, where the session
                      // token IS the PP token), tries a silent mint off the
                      // persisted refresh grant next, and only then opens
                      // PpLoginModal. This is a USER GESTURE, which is the only
                      // correct place to drive interactive re-auth from — see
                      // the "SILENT REFRESH ONLY" note on getAccessToken above.
                      //
                      // Gating the ACTION rather than the render is deliberate:
                      // `canCreateClaim` stays a pure persona check, so the
                      // button remains discoverable and the modal explains what
                      // is needed, instead of the entry point silently vanishing.
                      onClick: () => {
                        // Guard against a second click during a multi-second
                        // silent mint spawning another floating promise.
                        if (claimGatePendingRef.current) return;
                        claimGatePendingRef.current = true;
                        void (async () => {
                          try {
                            await ensurePpSession();
                            // Opens the ENTRY GATE, not the form. The gate
                            // verifies the member's health plan is one Astrana
                            // adjudicates, that the health plan code is actually
                            // known (a required non-empty PP-API param), and that
                            // the acting user holds a claim-write provider grant
                            // in this IPA — then hands back the chosen provider,
                            // which is the only thing that opens the form.
                            setClaimGateOpen(true);
                          } catch (e) {
                            // ensurePpSession also rejects with a plain Error
                            // when there is no signed-in identity email — a
                            // bare `catch { return }` would leave a DEAD BUTTON
                            // with no feedback. Only a deliberate cancel is
                            // silent. Mirrors the Create Auth onError handler
                            // in this same file; `.includes` (not `===`) for
                            // the same reason it gives.
                            const msg = e instanceof Error ? e.message : String(e);
                            if (!msg.includes(PP_LOGIN_CANCELLED_MESSAGE)) {
                              toast.error(
                                "Couldn't start a Provider Portal session. Please try again.",
                              );
                            }
                          } finally {
                            claimGatePendingRef.current = false;
                          }
                        })();
                      },
                    },
                  ],
                }
              : {}),
            ...(canPrintEob && eobAstranaId != null
              ? {
                  props: {
                    ...claimsModule.props,
                    // The button, the claim-detail fetch and the PDF now live in
                    // @astrana/ui behind this one flag; the module is off by
                    // default there. The persona grant is unchanged — same
                    // "printEob" action, new plumbing.
                    enableEobDownload: true,
                  },
                }
              : {}),
            // `as ModuleObject` is required, not cosmetic. ModuleObject is a
            // discriminated union (`type` ↔ its matching `props` shape); the two
            // conditional spreads above produce `props` as a union of "the
            // Claims props" and "the original props", which TS can no longer
            // correlate back to the `type` discriminant, so the literal widens
            // to a non-assignable union. Same reason the `m as ModuleObject`
            // narrowing cast above exists. The runtime shape is unchanged — only
            // Claims modules reach here, guarded by the ModuleType.CLAIMS check.
          } as ModuleObject;
        }),
      };
    }),
    // Notes — the chart's own visit notes, plus the entry point for drafting a
    // new one. This is the read-out half of the retired Encounter tab
    // (ACT-1241/1242) brought back deliberately scoped: `ChartNotesPanel` is
    // read-only and does NOT reuse `src/members/notes/ElationEncounterPanel.tsx`, which
    // still carries the per-section write-back grid that @astrana/ui's
    // one-review-one-write card replaced.
    //
    // DRAFTING LIVES HERE. "Create note" starts the flow on this tab; it used
    // to navigate to Scribe, which is the opposite of what the button says.
    //
    // Pass the human member id (memberPolicyId for Pathways / the PP member id)
    // — the same value the rest of the profile uses — NOT the route `id`, which
    // for Pathways members is the internal Mongo ObjectId and was leaking into
    // the panel header.
    //
    // BOTH ids come from `visitNoteMember`, not from two separate expressions.
    // `astranaId` is the key care-gql loads this note's lab results with, so a
    // memberId/astranaId pair that straddled two members would offer one
    // patient's labs inside another's note. One resolution, one member,
    // all-or-nothing — see `visitNoteMemberIdentity.ts`.
    // G1 (2026-08-19/20) — persona-gated ON TOP OF the feature flag: the flag
    // alone used to control this tab, which let every persona (including
    // CUSTOMER_SERVICE) see clinical note content. AC-ADM-3 keeps that AND
    // exactly as it was but moves the persona half to the shared
    // `useVisibleModules` pass below (catalog id "Notes"), instead of a separate
    // `notesAllowed` boolean here. Flag decides construction, gate decides
    // visibility — a persona without the Notes grant still sees nothing.
    ...(featureFlags.ambientScribe || featureFlags.aiVisitNotes
      ? [{ tabName: "Notes", modules: [
          <NotesTab
            key="notes-tab"
            memberId={visitNoteMember.memberId}
            astranaId={visitNoteMember.astranaId}
            encounter={encounterSnapshot ?? undefined}
            // Read from the LIVE bridge payload for the same reason as the write
            // guard's chart identity: the reports were read from the tab that is
            // on screen now, and the extension's own patient guard already
            // dropped anything belonging to another patient.
            imagingReports={elationClinical?.imagingReports}
            // The SAME chart identity the write guard gates on, reused as the
            // read's second patient lock.
            expectedEhrPatientId={openChartPatientId}
            onWriteSection={handleWriteVisitNoteSection}
            onDirtyChange={stableOnNoteDirtyChange}
          />,
        ] }]
      : []),
    // NOTE: the Encounter tab (live Elation chart read-out, ACT-1241/1242) was a
    // tech demo and is no longer surfaced. The panel itself lives on at
    // `src/members/notes/ElationEncounterPanel.tsx`; its read-out half came back as
    // `ChartNotesPanel` on the Notes tab, deliberately without the per-section
    // write-back grid that @astrana/ui's one-review-one-write card replaced.
    //
    // Scribe is the RECORD half only. Since @astrana/ui v0.4.90 the drafting
    // flow and its write seam live in <VisitNoteDrafter> (Notes tab) and this
    // panel produces no note and holds no seam — so the two tabs cannot produce
    // competing drafts for one encounter, which is what makes mounting both
    // safe. `chartTarget` is passed for session-reset identity ONLY: without it
    // a chart switch is invisible here (memberId is routinely undefined) and
    // one encounter's transcript stays on screen under another's header.
    //
    // AC-ADM-3: the flag below is now only HALF the gate. "Scribe" is a real
    // FEATURE_CATALOG tab id, so the `useVisibleModules` pass under this memo
    // additionally requires the persona to have been granted the `Scribe`
    // module. Flag AND persona — the flag alone no longer shows this tab, and
    // migration 0027 stages the grant to PROVIDER only, INACTIVE (dark), so
    // enabling the flag in an env does not by itself expose the panel.
    ...(featureFlags.ambientScribe || featureFlags.aiVisitNotes
      ? [{ tabName: "Scribe", modules: [
          <AmbientScribePanel
            key="ambient-scribe"
            memberId={handoffMemberIdHint || undefined}
            chartTarget={
              encounterSnapshot
                ? {
                    ehrPatientId: encounterSnapshot.ehrPatientId,
                    encounterKey: encounterSnapshot.encounterKey,
                  }
                : undefined
            }
          />,
        ] }]
      : []),
    {
      // AC-ADM-3: persona-gated like every other tab now — the catalog's
      // `Documents` entry keys off this same DOCUMENTS_TAB string, and the
      // `useVisibleModules` pass below drops the whole group when the active
      // persona lacks the grant. Migration 0027 grants it to all six personas
      // but ACTIVE only for PROVIDER + CUSTOMER_SERVICE, so this tab does
      // disappear for the other four by design.
      //
      // Documents tab renders inside MemberProfile's content area like any
      // other tab. MemberProfile renders raw React nodes in a tab's `modules`
      // array (see ModuleArray = (ModuleObject | ReactNode)[]), so we pass the
      // self-contained <MemberDocumentsPage/> directly — no overlay.
      tabName: DOCUMENTS_TAB,
      modules: [
        isPp ? (
          // PP members have no Pathways record for MemberDocumentsPage's
          // MEMBER_BY_ID self-fetch, so pass companyId/mpi explicitly.
          <MemberDocumentsPage
            key="documents"
            navId={id}
            companyId={ppCompanyId}
            membMpiNo={ppMpi}
            memberId={ppMemberId}
            userId={accountLabel ?? ""}
            // Submit PAF's memberKeyId needs PP's real GUID membKeyId — same
            // value already passed to the full-page Documents route below.
            mkey={ppMember?.membKeyId ?? undefined}
            selectedDocumentId={null}
            onSelectedDocumentIdChange={handleDocSelectionChange}
          />
        ) : (
          // `navId` is REQUIRED here, not a nicety: this branch relies entirely
          // on the MEMBER_BY_ID self-fetch, and under the keep-alive tab shell
          // MemberDocumentsPage's own `useParams().id` is always undefined
          // (App.tsx mounts the shell under a single catch-all `path="/*"`,
          // which declares no `:id`). Without it the self-fetch is skipped and
          // the tab renders nothing — the blank Documents tab bug. This page
          // already resolves the same id correctly via `navIdOverride`.
          <MemberDocumentsPage
            key="documents"
            navId={id}
            selectedDocumentId={null}
            onSelectedDocumentIdChange={handleDocSelectionChange}
          />
        ),
      ],
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // Resolution note (rebase onto dev): this is the UNION of dev's list and the
  // two visit-note deps. `handleWriteVisitNoteSection` in particular MUST stay
  // listed — it closes over the chart identity used for the fail-closed write
  // check, and a stale closure here would reintroduce exactly the wrong-chart
  // write this feature is built to prevent. `apolloClient` / `eobMember.*` were
  // dropped on dev by the EOB refactor and are deliberately not resurrected.
  ], [allModules, isPp, ppCompanyId, ppMpi, ppMemberId, accountLabel, resolvedAccessToken, ppReadAccessToken, ppReadEligible, stableEnsurePpReadSession, createAuthGateOpen, canCreateOutreach, stableResolvePpMemberId, stableOnAuthCreated, stableOnNewAuthDifferentMember, stableOnAuthDirtyChange, isDark, companyId, actingUserIpa, handoffMemberIdHint, id, nativeAuthMember, nativeUserId, nativeUserRole, selectedProvider, primarySessionToken, identityEmail, canPrintEob, eobAstranaId, canCreateClaim, createClaimOpen, claimProviderForMember, createClaimAction, claimMemberId, claimMemberCtx, claimUserId, claimUserRole, stableGetClaimAccessToken, stableOnClaimCreated, stableOnClaimClose, stableOnClaimError, stableOnClaimDirtyChange, stableOnNoteDirtyChange, stableOnClaimAnalytics, qualityGapChecklistActive, careManagementMarket, qualityGapClosedIds, onToggleQualityGap, qualityGapLoading, qualityGapReadOnly, encounterSnapshot, handleWriteVisitNoteSection,
    // The drafter's member identity, as its two PRIMITIVES rather than the
    // object (which is rebuilt every render and would defeat the memo). Both
    // are listed: dropping either would let the memo hold a rendered NotesTab
    // whose ids no longer match the member on screen.
    visitNoteMember.memberId, visitNoteMember.astranaId]);

  // THE persona visibility gate — one pass, over the COMPLETE tab list with the
  // flag-gated extras already merged in (AC-ADM-3). Nothing is appended after
  // this, so no tab can bypass it. Fails CLOSED: a persona with no
  // `persona_modules` row for a tab's module id does not get that tab, which is
  // why this code must not reach an environment before migration 0027 has been
  // applied there (it seeds the Diagnoses / Labs / Scribe / Documents grants).
  const visibleModulesWithExtras = useVisibleModules(allModulesWithExtras);

  // Admin-settable tab order: honor the persona's server-driven per-module
  // sortOrder, applied AFTER extras are merged (Notes/Scribe/Documents) so they
  // sort in-line with the rest of the tabs rather than being stranded, unordered,
  // at the tail (see useTabOrder's doc comment for the fail-safe — no persona
  // config → order unchanged from visibleModulesWithExtras). Every tab now
  // resolves to a ModuleId, so every tab is reorderable from the admin console.
  const orderedModules = useTabOrder(visibleModulesWithExtras);

  // Admin-configured landing tab (the "defaultTab" actions sentinel, mirroring
  // "collapsedByDefault"). Resolved against the FINAL sorted/visible tab order
  // so that, in the rare case more than one module carries the sentinel, the
  // first one in that order wins deterministically.
  const configuredDefaultTab = useConfiguredDefaultTab(orderedModules);
  const initialTab = useInitialTab(orderedModules, configuredDefaultTab);

  // Leaving a member lands on HOME (navh-home-updates): Home is the app's
  // start surface and carries the member-search widget, so there is nothing
  // to "resume" on the old Search page and no searchFilters to carry back.
  const handleBack = () => {
    // AM-5694 — don't blow away an in-progress Create-Auth form; W-leave — and
    // don't let an unfiled visit note slip away silently either. The navigation
    // itself is the continuation, so it runs after the note's dialog is answered
    // (or immediately, when nothing is dirty).
    guardedLeave(() => {
      // If the profile was opened from a surface that requested a return path
      // (e.g. the Practice Schedule), go back THERE — preserving its
      // day/provider via the carried query string — instead of Home.
      const from = effectiveState?.from;
      if (typeof from === "string" && from.startsWith("/schedule")) {
        navigate(from);
        return;
      }
      navigate("/home");
    });
  };

  // ACT-1303 — hold the whole profile behind the INITIAL currentUser load only
  // (loading AND no role resolved yet) so tabs and Home cards never render against
  // the null-persona allow-all fallback before the first persona payload arrives.
  // Once a role is known, a later `loading` pulse (background refetch / persona
  // switch) must NOT re-blank the page — that would unmount the embedded
  // MemberProfile subtree and its in-flight module queries. Gated here (after all
  // hooks) so hook order stays stable. Mirrors App.tsx ProtectedRoute.
  if (currentUserLoading && currentUserRole === null) {
    return <LoadingSpinner label="Loading member..." fullScreen={false} />;
  }

  // A PP member with neither companyId nor mpi can't be rendered. (AM-5716
  // follow-up) On a hard refresh with no mkey on the URL, @astrana/ui's
  // usePatientProfile has no `(companyId && mkey)` pair to resolve the
  // header with either — the URL alone can never supply a real membId (see
  // the PP URL-scheme comment above), so `(memberId && mpi)` can't be
  // satisfied yet at render time — hand that case to this host's own
  // not-found state (with a working "Back to Home") instead of ever
  // letting @astrana/ui reach its own unrecoverable dead-end. This is a
  // known, accepted degradation for a member whose PP row has no mkey (or
  // for a PP profile link bookmarked before this URL scheme carried mkey at
  // all) — no worse than today, since the normal (location.state) path
  // relies on mkey too.
  if (
    isPp &&
    isPpMemberUnresolvable({
      companyId: ppCompanyId,
      mpi: ppMpi,
      mkey: ppMkey,
      isRefresh: isPpRefresh,
    })
  ) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="rounded-xl border border-border bg-card px-5 py-6 text-center">
          <p className="text-base font-semibold text-foreground m-0">Member not found.</p>
          <button
            type="button"
            onClick={handleBack}
            className="mt-3 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // A connect-only fallback member with no astranaId can't be rendered.
  if (isAstranaOnly && !astranaOnlyId) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="rounded-xl border border-border bg-card px-5 py-6 text-center">
          <p className="text-base font-semibold text-foreground m-0">Member not found.</p>
          <button
            type="button"
            onClick={handleBack}
            className="mt-3 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!isPp && !isAstranaOnly && loading) {
    return <LoadingSpinner label="Loading member..." fullScreen={false} />;
  }

  if (!isPp && !isAstranaOnly && error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div
          role="alert"
          className="rounded-xl border border-destructive/40 bg-destructive/10 px-5 py-4 text-sm text-destructive"
        >
          <p className="font-semibold m-0 mb-1">Unable to load member</p>
          <p className="m-0">{error.message}</p>
          <button
            type="button"
            onClick={handleBack}
            className="mt-3 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!isPp && !isAstranaOnly && !data?.PathwaysMember) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="rounded-xl border border-border bg-card px-5 py-6 text-center">
          <p className="text-base font-semibold text-foreground m-0">Member not found.</p>
          <button
            type="button"
            onClick={handleBack}
            className="mt-3 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Connect-only fallback: hand the library just the astranaId so its existing
  // astranaId path (connectPatient(astranaId)) renders the header + panels.
  // PP: build the patient straight from the carried PP fields (mkey upper-cased
  // to mirror PP's Member360Panel). Pathways: map from the loaded record.
  const patient: PatientObject = isAstranaOnly
    ? { astranaId: astranaOnlyId }
    : isPp
      ? {
          memberId: ppMemberId,
          companyId: ppCompanyId || undefined,
          mkey: ppMkeyUpper,
          mpi: ppMpi,
          startDate: ppMember?.startDate ?? undefined,
        }
      : toPatientObject(data!.PathwaysMember!);

  // Full-page document detail: replaces the profile (header and all),
  // mirroring PP's document view. Back remounts the profile on Documents.
  if (openDocId !== null) {
    return (
      <div className="h-full min-h-0 overflow-y-auto px-6 py-4">
        {/* `navId` for the same reason as the in-tab call sites above — this
            full-page view is rendered from inside the same catch-all route, so
            MemberDocumentsPage cannot read `:id` from the router itself. */}
        {isPp ? (
          <MemberDocumentsPage
            navId={id}
            companyId={ppCompanyId}
            membMpiNo={ppMpi}
            memberId={ppMemberId}
            userId={accountLabel ?? ""}
            mkey={ppMember?.membKeyId ?? undefined}
            selectedDocumentId={openDocId}
            onSelectedDocumentIdChange={handleDocSelectionChange}
          />
        ) : (
          <MemberDocumentsPage
            navId={id}
            selectedDocumentId={openDocId}
            onSelectedDocumentIdChange={handleDocSelectionChange}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Module mount boundary. AppLayout's `main.flex-1.min-h-0` (and this
          div's own `flex-1 min-h-0`) already force this box to be EXACTLY the
          available viewport height, on the documented assumption that
          "MemberProfile already manages its own internal scroll, so we don't
          need the parent to grow" (see AppLayout.tsx). That assumption was
          being silently violated: on short-content tabs (e.g. the native
          Create-Auth form's confirmation screen), something inside the
          @astrana/ui subtree was rendering past this box's bottom edge with
          nothing here to stop it, so the un-clipped overflow propagated all
          the way up to the document, letting the user scroll past the
          module's sticky footer into blank dead space below it — even after
          two upstream (module-internal) height fixes. `overflow-hidden` here
          makes the host actually ENFORCE the boundary it already assumes:
          it's a no-op for every tab whose content fits (the common case) and
          for tabs with genuinely tall content (which scroll via the module's
          own internal `overflow-y-auto`, always sized to <= 100% of this
          box) — it only ever clips something that would otherwise spill past
          this box's edge, which is exactly the dead-space bug. Deliberately
          scoped to this one mount wrapper (not `<main>` or `<body>`) so nothing
          else in the app is affected. */}
      <div className="flex-1 min-h-0 relative overflow-hidden">
        <GraphQLProvider
          clientUrl={GRAPHQL_URL}
          authToken={token}
          onAuthFailure={onAuthFailure}
          identityKey={apolloIdentityKey}
        >
          {/* AM-5828 — the key deliberately carries SIGN-IN STATE, not the
              token's value. A silent MSAL renewal used to change this key and
              remount the whole subtree, wiping in-progress UI (a part-filled
              Create Claim form). @astrana/ui v0.4.90 reads the bearer live per
              request, so rotation needs no remount; identity swaps are handled
              by `identityKey` above, which repartitions the Apollo cache.
              `refreshKey` stays as the seam for a future non-destructive
              refresh, but AM-6260 removed its last bumper: remounting this
              subtree also wipes the module's post-submit confirmation screen
              and resets the active tab to Home, so neither `onAuthCreated` nor
              `onClaimCreated` may bump it. It is constant today. */}
          <GraphQLClientGate key={`${token ? "auth" : "anon"}:${refreshKey}`}>
            <MemberProfile
              patient={patient}
              visibleModules={orderedModules}
              // ACT-1488 (parity item T1) — `showInsurance` is deliberately NOT
              // passed. All three of Provider Portal's integration points
              // (Member360Panel, CreateAuthV2 LeftPanel, full-page /member) omit
              // it too, so every PP user sees the header insurance card via
              // @astrana/ui's own `showInsurance = true` default. Copilot used to
              // pass `isFrontDeskPersona` here (ACT-1306, front-desk-only), which
              // is why PROVIDER and ADMIN never saw the plan/coverage bar that PP
              // shows them. Omitting the prop — rather than passing `true` — is
              // what actually matches PP: the default IS the shared contract.
              // Needs no host data wiring; @astrana/ui's own usePatientProfile
              // fetches `insurance.memberId` via PATIENT_PROFILE_CONNECT_QUERY,
              // and its `hasInsurance` guard hides the bar when that is absent or
              // the "-" sentinel, so a member with no real coverage shows no
              // empty band.
              environment={ENVIRONMENT}
              initialTab={profileInitialTab !== INITIAL_TAB ? profileInitialTab : initialTab}
              onTabChange={(tab: string) => {
                // Leaving any tab closes the Create Claim form so the demo-mock
                // isolation flag (window.__ppClaimsDemoActive, derived from
                // createClaimOpen) clears. Otherwise it lingers `true` after the
                // user switches to the Authorizations tab, and the mock would
                // intercept the Create AUTH form's shared AuthMember/* lookups
                // (feeding it claim fixtures → RmiModDesc undefined → crash).
                // The form already unmounts on tab switch (only the active
                // module renders), so resetting loses no in-progress state.
                setCreateClaimOpen(false);
                // Same reset for the entry gate: leaving the tab abandons the
                // in-progress Create Claim, so the next attempt re-runs the
                // whole gate (grants + health-plan rules + provider choice).
                setClaimGateOpen(false);
                setClaimProvider(null);
                setClaimProviderFor(null);
                trackEvent("member_tab_viewed", { tab });
              }}
              // AM-5694: veto a tab switch while the Create Auth form is dirty
              // so the user isn't silently dropped out of an in-progress auth
              // (switching tabs unmounts the Auths tab). Reuses the same
              // window.confirm guard as the back-button paths; returns true
              // (allow) when nothing is dirty.
              //
              // W-leave: the visit note answers in the app's own dialog instead,
              // and a React dialog cannot answer a SYNCHRONOUS veto. So for the
              // note we return false — stopping the switch now — and hand
              // `resume` to the dialog, which performs the switch if the
              // clinician chooses to leave. `resume` bypasses this guard, so the
              // question is asked exactly once.
              //
              // `resume` is a NEW second argument to `onBeforeTabChange`, added
              // in @astrana/ui alongside `LeaveNotePrompt` and not in the pinned
              // release yet. Until the pin bump it arrives `undefined`, so that
              // case falls back to today's behaviour — the native confirm for
              // every dirty surface, note included. Nothing gets worse before the
              // bump; the note's dialog simply isn't reachable on this one path.
              // `resume` is annotated OPTIONAL on purpose, even though the
              // library declares it required: that is the honest type for the
              // pre-pin-bump runtime, where an older MemberProfile calls this
              // with one argument. It stays assignable to the library's
              // signature either way.
              onBeforeTabChange={(_nextTab: string, resume?: () => void) => {
                if (!resume) return confirmLeaveIfDirty();
                if (requestNoteLeave(resume)) return false;
                return confirmLeaveIfDirty();
              }}
              requestor={isPp ? "provider-portal" : "pathways"}
              assistant={{
                onSend: handleAssistantSend,
                suggestedQuestions: SUGGESTED_QUESTIONS,
                inputPlaceholder: "Ask about labs, auths, eligibility…",
              }}
              fullPage={false}
              reserveRightSpace={false}
              hidePrintPAF
              onSmsOpen={() => setSmsOpen(true)}
              onHeaderBackClick={handleBack}
              headerBackLabel="Back to Home"
              themeIsDark={isDark}
              // P6b — under the keep-alive tab shell the shared AppHeader
              // already provides the theme toggle + account/persona menu, so
              // suppress MemberProfile's own duplicate affordances (omitting
              // onThemeToggle/accountLabel/onSignOut collapses PatientHeader's
              // redundant actions menu — see PatientHeader's prop gating).
              // `themeIsDark` stays (dark styling is class-driven globally; the
              // prop only affects the now-hidden toggle's icon).
              onThemeToggle={hideGlobalChrome ? undefined : toggleTheme}
              accountLabel={hideGlobalChrome ? undefined : (accountLabel ?? undefined)}
              onSignOut={hideGlobalChrome ? undefined : () => void logout()}
            />
          </GraphQLClientGate>
        </GraphQLProvider>
      </div>
      <MemberSMS
        memberId={patient.memberId ?? ""}
        memberName={
          isPp ? (ppMember?.fullName ?? undefined) : (loadedMember?.name ?? undefined)
        }
        isOpen={smsOpen}
        onOpenChange={setSmsOpen}
        hideStrip
      />
      {/* AC-TRX-1 — live doctor↔patient interpreter rail. TWO gates, both
          required: the environment flag (VITE_FEATURE_TRANSLATOR, default off)
          AND the server-driven persona grant for the "Translator" module
          (`translatorAllowed` above). The flag decides whether an environment
          has the feature at all; the persona grant decides who inside it may
          interpret for a patient, and is configured in the DB / admin console
          rather than in this file.

          Mounted as a SIBLING of the profile mount boundary above, never
          inside it: the rail is a fixed, full-height overlay that portals
          itself to <body>, and the mount boundary is `overflow-hidden` with an
          @container ancestor inside @astrana/ui — exactly the containing-block
          /compositing-layer trap documented in CLAUDE.md (background fonts
          re-rasterize, and the strip would be clipped besides).

          Fails closed: with no credentials configured server-side the getters
          throw, `onError` fires, and the rail stays read-only — it never opens
          a vendor socket with missing credentials. */}
      {featureFlags.translator && translatorAllowed && (
        <Translator
          isOpen={translatorOpen}
          onOpenChange={setTranslatorOpen}
          // PP/connect-only members have no Pathways record; the hint is the
          // best available member key, and the prop is optional.
          memberId={handoffMemberIdHint || undefined}
          memberName={
            isPp ? (ppMember?.fullName ?? undefined) : (loadedMember?.name ?? undefined)
          }
          // Free-text upstream → always normalized to a roster tag; falls back
          // to Spanish when the member has no (or an unsupported) preference.
          patientLanguage={toBcp47(
            loadedMember?.languagePreferredSpoken ?? loadedMember?.languagePreferred,
          )}
          getGeminiToken={translatorCredentials.getGeminiToken}
          getElevenLabsSignedUrl={translatorCredentials.getElevenLabsSignedUrl}
          onError={(err: Error) => {
            // Same surface as the other module error paths in this file: a
            // toast for the clinician plus a PHI-free telemetry event (no
            // member identifiers, no transcript — ACT-1037).
            trackEvent("translator_error");
            toast.error(err.message);
          }}
        />
      )}
      {ppLoginOpen && identityEmail && (
        <PpLoginModal
          email={identityEmail}
          onSuccess={handlePpLoginSuccess}
          onCancel={handlePpLoginCancel}
        />
      )}
      {/* Create Claim ENTRY GATE. Mounted only while open so its PP-API
          claim-grant lookup fires on a user gesture and nowhere else, and
          rendered as a SIBLING of the profile mount boundary (it portals itself
          to <body>; see the Translator note above for why anything fixed and
          full-viewport must not live inside that @container subtree).
          `canCreateClaim` is re-checked here so a persona downgrade mid-session
          closes the gate too — it is a VISIBILITY gate that runs alongside, not
          instead of, the PP claim-write authorization the gate itself enforces.

          `isActiveTab` is REQUIRED, not defensive. Under the keep-alive tab shell
          (featureFlags.statefulTabShell) every open patient tab stays MOUNTED and
          is merely `hidden`; this overlay portals to <body>, which escapes that
          hiding. Without this gate, opening Create Claim on patient A and then
          clicking patient B's tab leaves A's modal covering B's profile — and
          Continue would mount A's claim form inside a hidden tab. Unmounting on
          deactivate also means re-activating re-runs the whole authorization
          check from scratch, which is the fail-closed direction. */}
      {canCreateClaim && isActiveTab && claimGateOpen && (
        <CreateClaimGate
          memberName={
            isPp ? (ppMember?.fullName ?? undefined) : (loadedMember?.name ?? undefined)
          }
          // The gate owns resolving the member to ONE Provider Portal row and
          // taking `membKeyId`/`hpCode`/`startDate` from it together; the host
          // only supplies what to resolve WITH. `ppRecord` is the PP-path
          // shortcut: a search row already carries that trio, row-atomic, for
          // the coverage span the user themselves picked — so no lookup runs.
          // It is passed unconditionally; an INCOMPLETE row (a hard-refreshed PP
          // profile, whose URL deliberately carries no health plan) is ignored
          // by the gate and resolution proceeds, which is how that path went
          // from "always blocked" to recoverable.
          client={apolloClient}
          member={claimResolveMember}
          ppRecord={isPp ? ppMember : undefined}
          companyId={companyId}
          ppApiBaseUrl={`${import.meta.env.VITE_PROVIDER_PORTAL_API_BASE_URL}/api`}
          getAccessToken={stableGetClaimAccessToken}
          // MUST come from the SAME identity chain as the bearer this request
          // carries, not from Create Auth's. `getAccessToken` above is
          // `stableGetClaimAccessToken` → `claimBearer` = `ppToken`, which has NO
          // ambient-Entra fallback (see the claimBearer doc above: an ambient
          // Entra bearer can never legitimately serve Create Claim), and PP-API
          // decodes `userId` from that same token.
          //
          // `nativeUserRole` is Create Auth's, derived from `identityToken`, which
          // DOES fall back to the ambient token when `!hasPpLink && !ppEligible` —
          // and `ppEligible` keys off Auths:create, not Claims:submit. So for a
          // Claims-only user who isn't PP-linked at render time, that chain can
          // yield an Entra-derived role (or default "PROVIDER") while the bearer
          // mint independently succeeds: correct userId, WRONG role. An actually
          // elevated user (SUPER ADMIN / QCIT / ADMINISTRATOR / CASE) would then
          // lose the SHOW_ALL_PROVIDERS_ROLES expansion and be hard-blocked —
          // fail-closed, so not a leak, but the same hard-block class this ticket
          // exists to remove.
          //
          // `claimUserRole` is the persona-derived value that tracks
          // claimBearer/claimUserId, and is what the @astrana/ui CreateClaim
          // module itself receives (:2141) — so this also keeps the gate and the
          // form it opens on one role. Optional by design; see CreateClaimGate's
          // userRole JSDoc.
          userRole={claimUserRole}
          onCancel={() => {
            setClaimGateOpen(false);
            setClaimProvider(null);
            setClaimProviderFor(null);
            setClaimSeed(null);
          }}
          onProceed={(provider, seed) => {
            setClaimProvider(provider);
            setClaimSeed(seed);
            // Bind the provider to the FULL decision it was just authorized
            // under — member + health plan + tenant — so it cannot survive a
            // switch-patient on the unkeyed classic route, nor a refetch that
            // changes the member or tenant underneath an open form.
            //
            // Keyed off the INCOMING seed, not `claimGateKeyNow`: that value is
            // computed from `claimSeed`, which is still null in this render pass,
            // so using it would store a key the very next render can no longer
            // reproduce — and the form's open condition would fail closed
            // forever.
            setClaimProviderFor(claimGateKey(claimMemberId, seed.hpCode, companyId));
            setClaimGateOpen(false);
            setCreateClaimOpen(true);
          }}
        />
      )}
      {/* "Link Provider Portal account" floating affordance — runs ALONGSIDE
          (not instead of) the in-place label swap injected into the Auths
          module's header `actions` above (see `showPpLinkAction`'s doc
          comment). Both are driven by the SAME debounced `showPpLinkAction`
          state, so they appear/disappear together: shown once eligible-but-
          not-linked has held for `PP_LINK_ACTION_DEBOUNCE_MS`, hidden once a
          real `ppToken` lands (or the identity was never eligible). Hidden
          while the modal itself is open to avoid a redundant floating
          affordance behind it. */}
      {showPpLinkAction && !ppLoginOpen && !ppLinkDismissed && identityEmail && (
        <div className="fixed bottom-4 right-4 z-40 flex max-w-sm items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-sm text-foreground shadow-lg">
          <span>Link your Provider Portal account to create authorizations.</span>
          <button
            type="button"
            onClick={handleLinkPpAccount}
            className="shrink-0 whitespace-nowrap rounded-md bg-primary px-3 py-1.5 font-medium text-primary-foreground hover:opacity-90"
          >
            Link account
          </button>
          <button
            type="button"
            onClick={() => setPpLinkDismissed(true)}
            aria-label="Dismiss"
            className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M3.5 3.5l7 7m0-7l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}
      {featureFlags.ehrSwitchConfirm && (
        <SwitchPatientModal
          candidate={switchCandidate}
          onStay={stayOnPatient}
          // AM-5706 — do NOT layer `confirmLeaveIfDirty()` (a `window.confirm`)
          // here: SwitchPatientModal is ITSELF an explicit confirmation
          // affordance (Stay / Navigate). Clicking "Navigate" is the user
          // already confirming the switch, so a second browser confirm on top
          // would double-prompt. The guard is kept on the paths that have no
          // such affordance (browser Back via useUnsavedChangesGuard, the
          // "Back to Home" button, and the EHR auto-switch — the flag-OFF
          // path that navigates silently with no modal).
          onNavigate={() => navigate("/members")}
        />
      )}
      {/*
        THE VISIT NOTE'S IN-APP LEAVE PROMPT — replaces the browser alert on
        every in-app leave path (profile tab switch, "Back to Home", browser
        Back/Forward, the EHR patient auto-switch). See `requestNoteLeave` above
        for the scope split and for why Create Auth / Create Claim keep theirs.

        Rendered here, at the page root, rather than inside the Notes tab: the
        dialog outlives the question. On a tab switch the answer decides whether
        the Notes tab unmounts at all, so a prompt living inside it would be
        asking a question about its own existence. It portals itself to <body>,
        so this position in the tree costs nothing.

        `onReviewBeforeWriting` is deliberately NOT passed. The review surface is
        a STAGE inside @astrana/ui's `VisitNoteEditor` and the shell has no way to
        drive it — offering the button would mean wiring a control that goes
        nowhere. The dialog then renders its two honest options: leave, or back to
        the note. Flagged in the report as Eric's to confirm.

        `draftDestination` is left at its "local" default because no host
        persists a visit-note draft yet (`VisitNoteDrafter` gets no
        `onSaveDraft` here — see the Notes tab wiring), so the draft really is in
        this browser tab only, and the copy says exactly that.
      */}
      {pendingNoteLeave && (
        <LeaveNotePrompt
          onLeave={() => {
            // Clear FIRST, then navigate: `proceed` may unmount this page, and a
            // setState after that is a no-op warning at best. The ref mirror
            // clears with it so the next leave request opens a fresh dialog.
            const { proceed } = pendingNoteLeave;
            pendingNoteLeaveRef.current = false;
            setPendingNoteLeave(null);
            proceed();
          }}
          onKeepEditing={() => {
            pendingNoteLeaveRef.current = false;
            setPendingNoteLeave(null);
          }}
        />
      )}
    </div>
  );
}
